// frontend/src/components/ContactsSidebar.jsx
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  socket as ioSocket,
  getConfirmedSocketIdentity,
  fetchUnreadForUsers,
} from '../services/socket';
import {
  getContacts,
  getPending as getPendingRequests,
  acceptRequest,
  rejectRequest,
  sendRequest,
  removeContact,
} from '../services/contactsApi';
import { openOrCreateConversation, getConversationMessages } from '../services/chatApi';
import { authenticatedRequest } from '../services/authApi';
import { searchUsers } from '../services/usersApi';
import { getAuthToken } from '../utils/auth';
import {
  normalizePresenceUserId,
  presenceStateRejectionReason,
  resolveContactIdentity,
} from '../utils/contactPresence';

const CONTACT_PRESENCE_DIAGNOSTICS = process.env.NODE_ENV === 'development';

function contactPresenceRuntimeDiagnosticsEnabled() {
  if (process.env.NODE_ENV === 'production') return false;
  try { return localStorage.getItem('z10:contacts_presence_trace') === '1'; } catch { return false; }
}

function logContactPresence(event, details) {
  if (!CONTACT_PRESENCE_DIAGNOSTICS) return;
  try { console.debug('[contacts-presence]', event, details); } catch {}
}

const CONTACT_PRESENCE_TRACE_STARTED_AT = typeof performance !== 'undefined' ? performance.now() : Date.now();

function traceContactPresence(event, details = {}) {
  try {
    const enabled = CONTACT_PRESENCE_DIAGNOSTICS || contactPresenceRuntimeDiagnosticsEnabled();
    if (!enabled) return;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    console.info('[contacts-presence-runtime]', {
      timestamp: new Date().toISOString(),
      elapsedMs: Math.round((now - CONTACT_PRESENCE_TRACE_STARTED_AT) * 10) / 10,
      event,
      socketId: ioSocket?.id || ioSocket?.__z10_lastSocketId || null,
      socketConnected: Boolean(ioSocket?.connected),
      authVersion: Number(ioSocket?.__z10_auth_version || 0),
      ...details,
    });
  } catch {}
}

// === Self ID helper (robust across keys) ===
function readSelfId() {
  try {
    const fromUserObj = (() => {
      try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
    })();
    const fromMeObj = (() => {
      try { return JSON.parse(localStorage.getItem('me') || 'null'); } catch { return null; }
    })();
    const id =
      (fromUserObj && (fromUserObj._id || fromUserObj.id || fromUserObj.userId)) ||
      (fromMeObj && (fromMeObj._id || fromMeObj.id || fromMeObj.userId)) ||
      localStorage.getItem('userId') ||
      null;
    return id ? String(id) : null;
  } catch { return null; }
}

function finitePresenceRevision(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function presenceOrderingReason(previous = {}, incoming = {}, snapshotRequestedAt = 0) {
  const previousRevision = finitePresenceRevision(previous?.revision);
  const incomingRevision = finitePresenceRevision(incoming?.revision);
  if (previousRevision !== null && incomingRevision !== null) {
    if (incomingRevision < previousRevision) return 'lower_revision';
    if (incomingRevision > previousRevision) return null;
  }
  return presenceStateRejectionReason(previous, incoming, snapshotRequestedAt);
}

const PRESENCE_CACHE_OWNER_KEY = 'csb_presence_owner';
const PRESENCE_CACHE_AUTH_GENERATION_KEY = 'csb_presence_auth_generation';

function committedPresenceAuthGeneration(socketLike = ioSocket) {
  const generation = Number(socketLike?.__z10_auth_generation);
  return Number.isFinite(generation) && generation >= 0 ? generation : null;
}

function presenceCacheSessionKey(ownerId, authGeneration) {
  const normalizedOwnerId = normalizePresenceUserId(ownerId);
  return normalizedOwnerId && Number.isFinite(authGeneration)
    ? `${normalizedOwnerId}:${authGeneration}`
    : null;
}

function pendingPresenceSessionKey(ownerId, socketId, authVersion) {
  const normalizedOwnerId = normalizePresenceUserId(ownerId);
  const normalizedSocketId = String(socketId || '').trim();
  const version = Number(authVersion);
  return normalizedOwnerId && normalizedSocketId && Number.isFinite(version)
    ? `${normalizedOwnerId}:pending:${normalizedSocketId}:${version}`
    : null;
}

function clearPresenceCacheStorage() {
  try {
    sessionStorage.removeItem('csb_presence');
    sessionStorage.removeItem(PRESENCE_CACHE_OWNER_KEY);
    sessionStorage.removeItem(PRESENCE_CACHE_AUTH_GENERATION_KEY);
  } catch {}
}

function readPresenceCacheForSession(ownerId, authGeneration) {
  try {
    const expectedSessionKey = presenceCacheSessionKey(ownerId, authGeneration);
    const cachedOwnerId = normalizePresenceUserId(sessionStorage.getItem(PRESENCE_CACHE_OWNER_KEY));
    const cachedAuthGeneration = Number(sessionStorage.getItem(PRESENCE_CACHE_AUTH_GENERATION_KEY));
    if (!expectedSessionKey || presenceCacheSessionKey(cachedOwnerId, cachedAuthGeneration) !== expectedSessionKey) return {};
    const raw = JSON.parse(sessionStorage.getItem('csb_presence') || '{}');
    if (!raw || typeof raw !== 'object') return {};
    const next = {};
    Object.keys(raw).forEach((k) => {
      const row = raw[k] || {};
      next[String(k)] = {
        online: typeof row.online === 'boolean' ? row.online : false,
        lastSeen: typeof row.lastSeen === 'number' ? row.lastSeen : null,
        deviceCount: typeof row.deviceCount === 'number' ? row.deviceCount : 0,
        _at: typeof row._at === 'number' ? row._at : 0,
        updatedAt: typeof row.updatedAt === 'number' ? row.updatedAt : null,
        revision: finitePresenceRevision(row.revision),
        // Browser cache is display-only until the first authoritative
        // snapshot for the currently confirmed socket identity arrives.
        _authoritative: false,
        onlineHoldUntil: typeof row.onlineHoldUntil === 'number' ? row.onlineHoldUntil : 0,
        pendingOfflineAt: typeof row.pendingOfflineAt === 'number' ? row.pendingOfflineAt : 0,
      };
    });
    return next;
  } catch {
    return {};
  }
}


// ---- helpers para extraer IDs y normalizar usuarios ----
function normalizeUser(u) {
  const identity = resolveContactIdentity(u, readSelfId());
  const id = identity.id;
  const base = identity.user || u;
  return {
    raw: u,
    _id: id ? String(id) : (base?._id ? String(base._id) : undefined),
    id: id ? String(id) : (base?.id ? String(base.id) : undefined),
    friendId: id ? String(id) : null,
    fullName: base?.fullName || 'Usuario',
    zodiacSign: base?.zodiacSign || '',
    photoPath: base?.photoPath || '',
    photoUrl: base?.photoUrl || base?.photoURL || '',
    avatarUrl: base?.avatarUrl || '',
    bio: base?.bio || '',
    isFriend: !!u?.isFriend,
    isPending: !!u?.isPending,
  };
}

function contactPhotoSrc(user) {
  try {
    const raw =
      user?.avatarUrl ||
      user?.photoUrl ||
      user?.photoURL ||
      user?.photoPath ||
      user?.photo ||
      '';

    const s = String(raw || '').trim();
    if (!s) return '/logo192.png';
    if (/^data:image\//i.test(s)) return s;
    if (/^https?:\/\//i.test(s)) return s;
    if (s.startsWith('/')) return s;
    return `/${s.replace(/^\/+/, '')}`;
  } catch {
    return '/logo192.png';
  }
}

export default function ContactsSidebar() {
  const { t } = useTranslation('common');
  const [friends, setFriends] = useState([]);   // [{friendId, fullName, ...}]
  const friendsRef = useRef([]);
  useEffect(() => { friendsRef.current = friends; }, [friends]);
  const [pending, setPending] = useState([]);   // [{__requestId, fromUserNorm}]
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const cardRef = useRef(null);

  // búsqueda
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);   // resultados de /api/users/search
  const [searching, setSearching] = useState(false);

  // presence map: { [friendId]: { online: boolean, lastSeen?: number, deviceCount?: number, _at?: number } }
  const [presence, setPresence] = useState({});
  const presenceStateRef = useRef(presence);
  const presenceCacheSessionRef = useRef(null);
  const initializedPresenceSessionKeyRef = useRef(null);
  const authoritativeSnapshotSessionKeyRef = useRef(null);
  const authenticatedPresenceOwnerRef = useRef(null);
  const presenceMutationSequenceRef = useRef(0);

  // === Snapshot throttle to avoid flapping ===
const snapInFlight = useRef(false);
const lastSnapAt = useRef(0);
const snapshotCoordinatorRef = useRef({ active: null, pending: null });
const snapshotCoordinatorMountedRef = useRef(false);
const authSessionGenerationRef = useRef(0);
const rootConfirmedIdentityRef = useRef(null);
const authSessionActiveRef = useRef(false);
const lastPresenceMutationSourceRef = useRef({ source: 'cached-session-storage', reason: null });
const previousRenderedPresenceRef = useRef(
  Object.fromEntries(Object.entries(presence || {}).map(([id, row]) => [id, row?.online === true])),
);

const mutatePresence = useCallback((source, update, details = {}) => {
  setPresence((previous) => {
    const result = typeof update === 'function' ? update(previous) : update;
    const next = result && typeof result === 'object' ? result : {};
    presenceMutationSequenceRef.current += 1;
    const mutation = {
      sequence: presenceMutationSequenceRef.current,
      source,
      reason: details.reason || null,
      requestId: details.requestId || null,
      authenticatedUserId: normalizePresenceUserId(readSelfId()),
      socketId: ioSocket?.id || null,
      authVersion: Number(ioSocket?.__z10_auth_version || 0),
      authGeneration: committedPresenceAuthGeneration(),
      sessionGeneration: authSessionGenerationRef.current,
      cacheSessionKey: presenceCacheSessionRef.current,
      initializedSessionKey: initializedPresenceSessionKeyRef.current,
      rootConfirmedIdentity: rootConfirmedIdentityRef.current,
      timestamp: Date.now(),
      ...details,
    };
    lastPresenceMutationSourceRef.current = mutation;
    presenceStateRef.current = next;
    traceContactPresence('presence-state:mutation', {
      ...mutation,
      previousIds: Object.keys(previous || {}),
      nextIds: Object.keys(next),
      contactChanges: Object.fromEntries(
        [...new Set([...Object.keys(previous || {}), ...Object.keys(next)])].map((id) => [
          id,
          { previous: previous?.[id] || null, incoming: details?.incomingRows?.[id] || null, result: next?.[id] || null },
        ])
      ),
    });
    return next;
  });
}, []);

const invalidateSnapshotCoordinator = useCallback(() => {
  const coordinator = snapshotCoordinatorRef.current;
  coordinator.pending = null;
  if (coordinator.active) {
    coordinator.active.finalized = true;
    if (coordinator.active.releaseTimer) clearTimeout(coordinator.active.releaseTimer);
    coordinator.active = null;
  }
  snapInFlight.current = false;
}, []);

useEffect(() => {
  snapshotCoordinatorMountedRef.current = true;
  return () => {
    snapshotCoordinatorMountedRef.current = false;
    authSessionActiveRef.current = false;
    invalidateSnapshotCoordinator();
  };
}, [invalidateSnapshotCoordinator]);

  // === Presence stability tuning (anti-flap) ===
  // We do NOT trust a single offline/online flip from the server because many backends
  // have delayed disconnect detection (mobile background, transport close, timeouts, etc.).
  // Strategy:
  // - When we see ONLINE, we keep it online for a short "sticky" window.
  // - When we see OFFLINE, we wait a grace period before showing offline.
  // This removes the classic: offline -> online (20-30s) -> offline oscillation.
  const OFFLINE_GRACE_MS = 4000;   // require offline to persist this long before flipping UI to offline

  function applyPresenceUpdate(prevRow, incoming, now) {
    const prev = prevRow && typeof prevRow === 'object' ? prevRow : {};

    const prevOnline = prev.online === true;
    const prevHoldUntil = typeof prev.onlineHoldUntil === 'number' ? prev.onlineHoldUntil : 0;
    const prevPendingOffAt = typeof prev.pendingOfflineAt === 'number' ? prev.pendingOfflineAt : 0;

    const hasOnlineFlag = !!incoming.hasOnlineFlag;
    const incomingOnline = hasOnlineFlag ? !!incoming.online : prevOnline;

    const ONLINE_STICKY_MS = 12000; // keep online briefly to avoid flapping

    // deviceCount / lastSeen from server (optional)
    const serverDeviceCount = typeof incoming.deviceCount === 'number' ? incoming.deviceCount : (prev.deviceCount ?? 0);
    const serverLastSeen = typeof incoming.lastSeen === 'number' ? incoming.lastSeen : null;

    // Base row we will evolve
    let next = {
      ...prev,
      deviceCount: serverDeviceCount,
    };

    // Always stamp _at when we receive any presence-related payload for that user
    next._at = now;

    if (hasOnlineFlag && !incomingOnline) {
  // An explicit server row is authoritative. Grace is reserved for ambiguous
  // local transport signals, never a presence:users or presence:user offline.
  if (incoming.authoritative || typeof serverLastSeen === 'number') {
    return {
      ...prev,
      online: false,
      onlineHoldUntil: 0,
      pendingOfflineAt: 0,
      lastSeen: serverLastSeen,
      deviceCount: serverDeviceCount,
      _at: now,
    };
  }

  // ⚠️ Offline dudoso (transport close, timeout): aplicar grace
  if (!prevPendingOffAt) next.pendingOfflineAt = now;

  next.lastSeen =
    serverLastSeen ??
    (typeof prev.lastSeen === 'number' ? prev.lastSeen : null);

  if (prevHoldUntil && now < prevHoldUntil) {
    next.online = true;
    return next;
  }

  next.online = prevOnline;
  return next;
}

    // No explicit online flag: keep current online state, but accept deviceCount/lastSeen if present
    if (serverLastSeen != null && !incomingOnline) {
      next.lastSeen = serverLastSeen;
    }

    // ✅ ONLINE update
        if (hasOnlineFlag && incomingOnline) {
      return {
        ...prev,
        online: true,
        // keep a short sticky window to avoid online/offline oscillation
        onlineHoldUntil: now + ONLINE_STICKY_MS,
        pendingOfflineAt: 0,
        // IMPORTANT: while online, never carry a stale cached lastSeen.
        lastSeen: null,
        deviceCount: serverDeviceCount,
        _at: now,
      };
    }
    if (prevHoldUntil) next.onlineHoldUntil = prevHoldUntil;
    if (prevPendingOffAt) next.pendingOfflineAt = prevPendingOffAt;
    next.online = prevOnline;
    return next;
  }

  // Presence payload coercion and merge helpers (memoized)
  const coercePresencePayload = useCallback((resp) => {
    // presence:users is intentionally read from its explicit map. The server
    // also mirrors rows at top level for legacy clients; consuming that wrapper
    // risks treating metadata as a contact or a missing row as offline.
    if (!resp || typeof resp !== 'object' || !resp.users || typeof resp.users !== 'object') return {};
    const base = resp.users;
    if (Array.isArray(base.online)) {
      const out = {};
      base.online.forEach((id) => { out[String(id)] = { online: true }; });
      Object.keys(base).forEach((k) => {
        if (k === 'online') return;
        out[String(k)] = base[k];
      });
      return out;
    }
    return base;
  }, []);

  const mergePresence = useCallback((prev, payload, requestedIds = [], snapshotRequestedAt = 0) => {
    const now = Date.now();
    const next = { ...prev };

    const map = coercePresencePayload(payload);
    // For explicit snapshots (presence:users), if the server omits an id we requested,
    // we treat it as OFFLINE. This avoids “stuck online” until refresh.
    const returnedIds = new Set(Object.keys(map || {}).map(normalizePresenceUserId).filter(Boolean));

    // 1) Merge everything the server explicitly returned
    Object.keys(map).forEach((rawId) => {
      const id = normalizePresenceUserId(rawId);
      if (!id || id === 'ok' || id === 'users') return;
      const row = map[rawId];
      const prevRow = prev[id] || {};
      const rejectionReason = prevRow?._authoritative === false
        ? null
        : presenceOrderingReason(prevRow, row, snapshotRequestedAt);
      const renderedFriendId = (friendsRef.current || []).find(
        (friend) => normalizePresenceUserId(friend?.friendId) === id
      )?.friendId || null;
      if (rejectionReason) {
        traceContactPresence('snapshot:merge-rejected', {
          rejectReason: rejectionReason,
          presenceUserId: id,
          rawId,
          normalizedContactId: id,
          renderedFriendId: renderedFriendId ? String(renderedFriendId) : null,
          previousRow: prevRow,
          incomingRow: row,
          snapshotRequestedAt,
          snapshotUpdatedAt: Number(row?.updatedAt || 0),
          previousUpdatedAt: Number(prevRow?.updatedAt || 0),
        });
        logContactPresence('snapshot:rejected', {
          presenceUserId: id,
          snapshotRequestedAt,
          snapshotUpdatedAt: Number(row?.updatedAt || 0),
          liveReceivedAt: Number(prevRow?._liveReceivedAt || 0),
          finalOnline: prevRow?.online === true,
          rejectionReason,
        });
        return;
      }

      // IMPORTANT:
      // - Only treat as online if server explicitly says so (boolean true or {online:true}).
      // - If server doesn't include an online flag, keep previous online state.
      const hasOnlineFlag =
        (typeof row === 'boolean') ||
        (row && (
          Object.prototype.hasOwnProperty.call(row, 'online') ||
          Object.prototype.hasOwnProperty.call(row, 'connected')
        ));

      const incomingOnline = hasOnlineFlag
        ? !!(
            typeof row === 'boolean'
              ? row
              : (Object.prototype.hasOwnProperty.call(row, 'online') ? row.online : row.connected)
          )
        : undefined;

      const incomingLastSeen = (row && typeof row.lastSeen === 'number') ? row.lastSeen : null;
      const incomingDeviceCount = typeof (row && row.deviceCount) === 'number'
        ? row.deviceCount
        : (prevRow?.deviceCount ?? 0);

      next[id] = applyPresenceUpdate(prevRow, {
        hasOnlineFlag,
        online: incomingOnline,
        lastSeen: incomingLastSeen,
        deviceCount: incomingDeviceCount,
        authoritative: hasOnlineFlag && incomingOnline === false,
      }, now);
      next[id].updatedAt = Number(row?.updatedAt || prevRow.updatedAt || 0);
      const incomingRevision = finitePresenceRevision(row?.revision);
      const previousRevision = finitePresenceRevision(prevRow?.revision);
      next[id].revision = incomingRevision ?? previousRevision;
      next[id]._authoritative = true;
      traceContactPresence('snapshot:applied', {
        userId: id,
        rawId,
        normalizedContactId: id,
        renderedFriendId: renderedFriendId ? String(renderedFriendId) : null,
        reason: lastPresenceMutationSourceRef.current?.reason || null,
        snapshotRequestedAt,
        incomingOnline,
        incomingUpdatedAt: Number(row?.updatedAt || 0),
        incomingRevision,
        previousOnline: prevRow?.online === true,
        previousUpdatedAt: Number(prevRow?.updatedAt || 0),
        previousRevision,
        applied: true,
        rejectionReason: null,
        finalRow: next[id],
      });
    });

    // Missing snapshot rows are unknown, never offline. Preserve an existing
    // state and only initialize unknown visual rows without an online flag.
    const req = Array.isArray(requestedIds) ? requestedIds.map(normalizePresenceUserId).filter(Boolean) : [];
    req.forEach((rid) => {
      const prevRow = prev[rid] || {};

      if (!returnedIds.has(rid)) {
        if (!next[rid]) next[rid] = { online: false, lastSeen: null, deviceCount: 0, _at: 0, _authoritative: false };
        return;
      }

      // Otherwise, only initialize missing entries without forcing online/offline.
      if (next[rid]) return;
      next[rid] = {
        online: !!prevRow.online,
        lastSeen: null,
        deviceCount: prevRow?.deviceCount ?? 0,
        _at: typeof prevRow._at === 'number' ? prevRow._at : 0,
        _authoritative: prevRow?._authoritative === true,
      };
    });

    return next;
  }, [coercePresencePayload]);

  const snapshotPresence = useCallback((ids, options = {}) => {
    const s = ioSocket;
    const list = Array.isArray(ids) ? [...new Set(ids.map(normalizePresenceUserId).filter(Boolean))] : [];
    const requestedSocketId = options?.socketId ? String(options.socketId) : null;
    const expectedUserId = normalizePresenceUserId(options?.expectedUserId || readSelfId());
    const confirmedUserId = normalizePresenceUserId(options?.confirmedUserId);
    const authVersion = Number(options?.authVersion || 0);
    const sessionGeneration = Number.isFinite(options?.sessionGeneration)
      ? options.sessionGeneration
      : authSessionGenerationRef.current;
    const reason = options?.reason || 'other';
    traceContactPresence('snapshot:invoked', {
      reason,
      socketId: s?.id || null,
      socketConnected: Boolean(s?.connected),
      sessionGeneration,
      requestedIds: list,
    });
    if (!s || !s.connected || !expectedUserId || expectedUserId !== confirmedUserId || !snapshotCoordinatorMountedRef.current || !authSessionActiveRef.current) {
      traceContactPresence('snapshot:skipped', {
        reason,
        skipReason: !s ? 'no_socket' : !s.connected ? 'socket_disconnected' :
          !snapshotCoordinatorMountedRef.current ? 'component_unmounted' : 'inactive_auth_session',
        socketId: s?.id || null,
        sessionGeneration,
      });
      return false;
    }
    if (!list.length) {
      traceContactPresence('snapshot:skipped', { reason, skipReason: 'no_contact_ids', socketId: s.id || null, sessionGeneration });
      return false;
    }

    const coordinator = snapshotCoordinatorRef.current;
    const request = { list, socketId: requestedSocketId, expectedUserId, confirmedUserId, authVersion, sessionGeneration, reason, finalized: false, releaseTimer: null };
    const sameRequestedIds = (left, right) =>
      left.length === right.length && left.every((id, index) => id === right[index]);

    const dispatch = (current) => {
      if (
        !snapshotCoordinatorMountedRef.current ||
        !authSessionActiveRef.current ||
        !s.connected ||
        current.sessionGeneration !== authSessionGenerationRef.current ||
        current.expectedUserId !== normalizePresenceUserId(readSelfId()) ||
        current.confirmedUserId !== current.expectedUserId ||
        current.socketId !== String(s.id || '') ||
        current.authVersion !== Number(s.__z10_auth_version || 0)
      ) {
        traceContactPresence('snapshot:dispatch-skipped', {
          reason: current.reason,
          skipReason: current.sessionGeneration !== authSessionGenerationRef.current
            ? 'wrong_generation'
            : !s.connected ? 'socket_disconnected' : 'identity_or_auth_changed',
          socketId: s?.id || null,
          sessionGeneration: current.sessionGeneration,
          currentSessionGeneration: authSessionGenerationRef.current,
        });
        return false;
      }
      const reqTs = Date.now();
      current.reqTs = reqTs;
      current.dispatchAt = reqTs;
      current.dispatchSocketId = String(s.id || '');
      coordinator.active = current;
      snapInFlight.current = true;

      const finalize = () => {
        if (current.finalized) {
          traceContactPresence('snapshot:finalize-ignored', {
            reason: current.reason,
            ignoreReason: 'already_finalized',
            socketId: s?.id || null,
            sessionGeneration: current.sessionGeneration,
          });
          return;
        }
        current.finalized = true;
        if (current.releaseTimer) clearTimeout(current.releaseTimer);
        if (coordinator.active === current) coordinator.active = null;
        snapInFlight.current = false;

        const pending = coordinator.pending;
        coordinator.pending = null;
        if (
          pending &&
          snapshotCoordinatorMountedRef.current &&
          pending.sessionGeneration === authSessionGenerationRef.current
        ) dispatch(pending);
      };

      logContactPresence('snapshot:request', {
        authenticatedUserId: normalizePresenceUserId(readSelfId()),
        requestedContactIds: current.list,
      });

      try {
        traceContactPresence('snapshot:dispatch', {
          reason: current.reason,
          socketId: s.id || null,
          sessionGeneration: current.sessionGeneration,
          requestedIds: current.list,
          requestedAt: reqTs,
          dispatchAt: current.dispatchAt,
          capturedSocketId: current.dispatchSocketId || null,
        });
        // The server contract always ACKs this authenticated request. Do not
        // wrap it in Socket.IO's short client timeout: on a cold Redis/adapter
        // path that timeout discards a correct late ACK even though the
        // identity tuple is still current. Disconnect/session invalidation
        // already cancels coordinator ownership deterministically.
        s.emit('presence:users', current.list, (...ackArgs) => {
          // Preserve compatibility with the historical two-argument test/
          // wrapper callback while production Socket.IO supplies one ACK value.
          const twoArgumentAck = ackArgs.length > 1;
          const resp = twoArgumentAck ? ackArgs[1] : ackArgs[0];
          const err = twoArgumentAck
            ? ackArgs[0]
            : (resp?.ok === false ? new Error(resp?.error || 'presence_snapshot_failed') : null);
          if (current.finalized) {
            traceContactPresence('snapshot:callback-ignored', {
              reason: current.reason,
              ignoreReason: 'already_finalized',
              socketId: s?.id || null,
              sessionGeneration: current.sessionGeneration,
            });
            return;
          }
          const returned = coercePresencePayload(resp);
          const responseAt = Date.now();
          const responseSocketId = String(s?.id || '');
          const belongsToCurrentSocket = Boolean(
            current.dispatchSocketId &&
            responseSocketId &&
            current.dispatchSocketId === responseSocketId
          );
          traceContactPresence('snapshot:response', {
            reason: current.reason,
            socketId: s?.id || null,
            sessionGeneration: current.sessionGeneration,
            requestedIds: current.list,
            returnedIds: Object.keys(returned || {}).map(normalizePresenceUserId).filter(Boolean),
            returnedStates: Object.fromEntries(Object.entries(returned || {}).map(([id, state]) => [
              normalizePresenceUserId(id),
              typeof state === 'boolean' ? state : state?.online === true,
            ]).filter(([id]) => Boolean(id) && id !== 'ok' && id !== 'users')),
            returnedUpdatedAt: Object.fromEntries(Object.entries(returned || {}).map(([id, state]) => [
              normalizePresenceUserId(id),
              Number(state?.updatedAt || 0),
            ]).filter(([id]) => Boolean(id) && id !== 'ok' && id !== 'users')),
            returnedRows: Object.entries(returned || {}).map(([id, state]) => ({
              userId: normalizePresenceUserId(id), online: typeof state === 'boolean' ? state : state?.online,
              status: state?.status || null, updatedAt: Number(state?.updatedAt || 0),
              revision: finitePresenceRevision(state?.revision), deviceCount: Number(state?.deviceCount || 0),
            })).filter((row) => Boolean(row.userId)),
            rowStatesBefore: Object.fromEntries(current.list.map((id) => {
              const normalizedId = normalizePresenceUserId(id);
              const row = presenceStateRef.current?.[normalizedId] || {};
              return [normalizedId, {
                online: row?.online === true,
                updatedAt: Number(row?.updatedAt || 0),
              }];
            }).filter(([id]) => Boolean(id))),
            acknowledgementError: err?.message || null,
            acknowledgementOutcome: err ? (err?.message?.toLowerCase().includes('timeout') ? 'timeout' : 'backend_error') :
              (!resp || typeof resp !== 'object' ? 'invalid_response' : 'ack_success'),
            requestedAt: reqTs,
            dispatchAt: current.dispatchAt,
            responseAt,
            capturedSocketId: current.dispatchSocketId || null,
            rawResponse: resp,
            rawUsers: resp?.users,
            rawRows: Object.fromEntries(current.list.map((id) => [id, resp?.users?.[id] ?? resp?.[id] ?? null])),
          });
          traceContactPresence('presence-sync:response', {
            expectedUserId: current.expectedUserId,
            confirmedUserId: current.confirmedUserId,
            sessionGeneration: current.sessionGeneration,
            socketId: current.socketId,
            authVersion: current.authVersion,
            requestedIds: current.list,
            reason: current.reason,
            acknowledgementError: err?.message || null,
          });
          if (
            !err &&
            snapshotCoordinatorMountedRef.current &&
          current.sessionGeneration === authSessionGenerationRef.current &&
            belongsToCurrentSocket &&
            current.expectedUserId === normalizePresenceUserId(readSelfId()) &&
            current.confirmedUserId === current.expectedUserId &&
            current.authVersion === Number(s.__z10_auth_version || 0)
          ) {
            const responseMap = returned;
            logContactPresence('snapshot:response', {
              authenticatedUserId: normalizePresenceUserId(readSelfId()),
              results: Object.fromEntries(Object.entries(responseMap).map(([id, state]) => [
                normalizePresenceUserId(id),
                state?.status || (state?.online ? 'online' : 'offline'),
              ]).filter(([id]) => Boolean(id))),
            });
            // only apply if this snapshot is the newest
            if (reqTs >= lastSnapAt.current) {
              traceContactPresence('snapshot:applied', {
                reason: current.reason,
                socketId: s?.id || null,
                sessionGeneration: current.sessionGeneration,
                requestedAt: reqTs,
                previousLastSnapAt: lastSnapAt.current,
              });
              lastSnapAt.current = reqTs;
              authoritativeSnapshotSessionKeyRef.current = initializedPresenceSessionKeyRef.current;
              mutatePresence('snapshot', (prev) => mergePresence(prev, resp, current.list, reqTs), {
                reason: current.reason,
                requestId: `${current.dispatchSocketId}:${reqTs}`,
                socketId: current.dispatchSocketId,
                authVersion: current.authVersion,
                sessionGeneration: current.sessionGeneration,
                timestamp: responseAt,
                incomingRows: responseMap,
              });
            } else {
              traceContactPresence('snapshot:response-ignored', {
                reason: current.reason,
                ignoreReason: 'older_snapshot_order',
                socketId: s?.id || null,
                sessionGeneration: current.sessionGeneration,
                requestedAt: reqTs,
                lastSnapAt: lastSnapAt.current,
              });
            }
          } else if (!err) {
            traceContactPresence('snapshot:response-ignored', {
              reason: current.reason,
              ignoreReason: !belongsToCurrentSocket
                ? 'stale_socket'
                : current.sessionGeneration !== authSessionGenerationRef.current
                  ? 'wrong_generation'
                  : 'component_unmounted_or_inactive',
              socketId: s?.id || null,
              capturedSocketId: current.dispatchSocketId || null,
              sessionGeneration: current.sessionGeneration,
              currentSessionGeneration: authSessionGenerationRef.current,
            });
          }
          finalize();
        });
        return true;
      } catch {
        finalize();
        return false;
      }
    };

    if (coordinator.active) {
      if (
        coordinator.active.sessionGeneration === request.sessionGeneration &&
        coordinator.active.expectedUserId === request.expectedUserId &&
        coordinator.active.confirmedUserId === request.confirmedUserId &&
        coordinator.active.authVersion === request.authVersion &&
        coordinator.active.socketId === request.socketId &&
        sameRequestedIds(coordinator.active.list, request.list) &&
        true
      ) {
        traceContactPresence('snapshot:deduplicated', {
          reason,
          dedupeReason: 'matching_active_request',
          socketId: s.id || null,
          sessionGeneration,
        });
        return 'active';
      }
      const previousPending = coordinator.pending;
      coordinator.pending = {
        ...request,
        socketId: requestedSocketId || previousPending?.socketId || null,
      };
      traceContactPresence('snapshot:queued', {
        reason,
        socketId: s.id || null,
        sessionGeneration,
        requestedIds: list,
      });
      return 'queued';
    }

    return dispatch(request);
  }, [coercePresencePayload, mergePresence, mutatePresence]);
  const snapshotPresenceRef = useRef(snapshotPresence);
  useEffect(() => { snapshotPresenceRef.current = snapshotPresence; }, [snapshotPresence]);

  // All snapshot entry points funnel through this identity boundary. A focus
  // event may queue work, but it can never put presence:users on the wire
  // until the server ACK has confirmed this account on this transport.
  const syncContactsPresence = useCallback(async (reason) => {
    const sessionGeneration = authSessionGenerationRef.current;
    const expectedUserId = normalizePresenceUserId(readSelfId());
    const tokenAtStart = getAuthToken() || null;
    const ids = [...new Set((friendsRef.current || []).map((friend) => normalizePresenceUserId(friend?.friendId)).filter(Boolean))];
    if (!expectedUserId || !tokenAtStart || !ids.length || !snapshotCoordinatorMountedRef.current) return false;
    traceContactPresence('presence-sync:queued', { expectedUserId, confirmedUserId: null, sessionGeneration, socketId: ioSocket?.id || null, authVersion: Number(ioSocket?.__z10_auth_version || 0), requestedIds: ids, reason });
    const identity = getConfirmedSocketIdentity(expectedUserId);
    if (!identity) {
      traceContactPresence('presence-sync:ignored', { expectedUserId, confirmedUserId: null, sessionGeneration, socketId: ioSocket?.id || null, authVersion: Number(ioSocket?.__z10_auth_version || 0), requestedIds: ids, reason, ignoreReason: 'identity_not_confirmed' });
      return false;
    }
    const rootIdentity = rootConfirmedIdentityRef.current;
    const rootIdentityMatches = Boolean(
      rootIdentity && rootIdentity.userId === identity.userId &&
      rootIdentity.socketId === identity.socketId && rootIdentity.authVersion === identity.authVersion
    );
    if (!rootIdentityMatches) {
      traceContactPresence('presence-sync:ignored', { expectedUserId, confirmedUserId: identity.userId, sessionGeneration, socketId: identity.socketId, authVersion: identity.authVersion, requestedIds: ids, reason, ignoreReason: 'root_identity_not_confirmed' });
      return false;
    }
    const latestIds = [...new Set((friendsRef.current || []).map((friend) => normalizePresenceUserId(friend?.friendId)).filter(Boolean))];
    const stillCurrent = snapshotCoordinatorMountedRef.current && authSessionActiveRef.current &&
      sessionGeneration === authSessionGenerationRef.current && expectedUserId === normalizePresenceUserId(readSelfId()) &&
      tokenAtStart === (getAuthToken() || null) &&
      identity.userId === expectedUserId && identity.socketId === String(ioSocket?.id || '') && identity.authVersion === Number(ioSocket?.__z10_auth_version || 0);
    if (!stillCurrent || !latestIds.length) return false;
    const active = snapshotCoordinatorRef.current.active;
    if (active && (active.sessionGeneration !== sessionGeneration || active.expectedUserId !== expectedUserId ||
      active.confirmedUserId !== identity.userId || active.socketId !== identity.socketId || active.authVersion !== identity.authVersion)) {
      // A response has no cancellation primitive, but removing ownership now
      // means its callback can neither block nor mutate this identity.
      invalidateSnapshotCoordinator();
    }
    traceContactPresence('presence-sync:dispatch', { expectedUserId, confirmedUserId: identity.userId, sessionGeneration, socketId: identity.socketId, authVersion: identity.authVersion, requestedIds: latestIds, reason });
    return snapshotPresenceRef.current(latestIds, { reason, sessionGeneration, expectedUserId, confirmedUserId: identity.userId, socketId: identity.socketId, authVersion: identity.authVersion });
  }, [invalidateSnapshotCoordinator]);
  const syncContactsPresenceRef = useRef(syncContactsPresence);
  useEffect(() => { syncContactsPresenceRef.current = syncContactsPresence; }, [syncContactsPresence]);

// === Presence stability sweep (anti-flap) ===
// Finalizes pending offline transitions and expires online sticky holds.
useEffect(() => {
  const t = setInterval(() => {
    const now = Date.now();
    mutatePresence('presence-stability-sweep', (prev) => {
      if (!prev || typeof prev !== 'object') return prev;
      let changed = false;
      const next = { ...prev };

      Object.keys(next).forEach((k) => {
        const row = next[k];
        if (!row || typeof row !== 'object') return;

        const holdUntil = typeof row.onlineHoldUntil === 'number' ? row.onlineHoldUntil : 0;
        const pendingOffAt = typeof row.pendingOfflineAt === 'number' ? row.pendingOfflineAt : 0;

        // If we are online but hold has expired, allow demotion (if no newer online came in)
        if (row.online === true && holdUntil && now >= holdUntil) {
          // If there is a pending offline and grace has passed, finalize offline.
          if (pendingOffAt && now - pendingOffAt >= OFFLINE_GRACE_MS) {
            next[k] = {
              ...row,
              online: false,
              onlineHoldUntil: 0,
              pendingOfflineAt: 0,
              lastSeen: typeof row.lastSeen === 'number' ? row.lastSeen : null,
            };
            changed = true;
            return;
          }

          // An authoritative server-online row remains online until an
          // ordered server row/event says otherwise. Elapsed browser time is
          // not presence authority.
          return;
        }

        // If we have a pending offline and grace has passed, finalize offline (even if row is still online)
        if (pendingOffAt && now - pendingOffAt >= OFFLINE_GRACE_MS) {
          if (row.online !== false || row.onlineHoldUntil || row.pendingOfflineAt) {
            next[k] = {
              ...row,
              online: false,
              onlineHoldUntil: 0,
              pendingOfflineAt: 0,
              lastSeen: typeof row.lastSeen === 'number' ? row.lastSeen : null,
            };
            changed = true;
          }
        }
      });

      return changed ? next : prev;
    }, { reason: 'pending-offline-grace' });
  }, 3000);

  return () => clearInterval(t);
}, [mutatePresence]);


// Persistir presencia entre pantallas (evita parpadeo al volver desde Home)
useEffect(() => {
  presenceStateRef.current = presence;
  try {
    const ownerId = normalizePresenceUserId(readSelfId());
    const authGeneration = committedPresenceAuthGeneration();
    const currentSessionKey = presenceCacheSessionKey(ownerId, authGeneration);
    // Do not write the empty transition state while waiting for the root
    // authenticated identity. That would overwrite an exact-session cache
    // before it has had a chance to hydrate.
    if (!currentSessionKey || presenceCacheSessionRef.current !== currentSessionKey) return;
    sessionStorage.setItem(PRESENCE_CACHE_OWNER_KEY, ownerId);
    sessionStorage.setItem(PRESENCE_CACHE_AUTH_GENERATION_KEY, String(authGeneration));
    sessionStorage.setItem('csb_presence', JSON.stringify(presence || {}));
  } catch {}
}, [presence]);

useEffect(() => {
  const previous = previousRenderedPresenceRef.current || {};
  const next = Object.fromEntries(Object.entries(presence || {}).map(([id, row]) => [id, row?.online === true]));
  Object.keys(next).forEach((contactId) => {
    if (Object.prototype.hasOwnProperty.call(previous, contactId) && previous[contactId] === next[contactId]) return;
    traceContactPresence('ui-presence-row-changed', {
      contactId,
      previousOnline: Object.prototype.hasOwnProperty.call(previous, contactId) ? previous[contactId] : null,
      nextOnline: next[contactId],
      source: lastPresenceMutationSourceRef.current?.source || 'unknown',
      lastSnapshotReason: lastPresenceMutationSourceRef.current?.reason || null,
      mutation: lastPresenceMutationSourceRef.current,
      renderedRow: presence?.[contactId] || null,
      updatedAt: Number(presence?.[contactId]?.updatedAt || 0),
      sessionGeneration: authSessionGenerationRef.current,
    });
  });
  previousRenderedPresenceRef.current = next;
}, [presence]);

useEffect(() => {
  (friends || []).forEach((friend) => {
    const rawFriendId = friend?.friendId;
    const normalizedFriendId = normalizePresenceUserId(rawFriendId);
    traceContactPresence('presence-render:lookup', {
      rawFriendId: rawFriendId == null ? null : String(rawFriendId),
      normalizedFriendId,
      presenceKeys: Object.keys(presence || {}),
      rawLookupRow: rawFriendId == null ? null : (presence?.[String(rawFriendId)] || null),
      normalizedLookupRow: normalizedFriendId ? (presence?.[normalizedFriendId] || null) : null,
      renderedOnline: Boolean(normalizedFriendId && presence?.[normalizedFriendId]?.online === true),
      mutation: lastPresenceMutationSourceRef.current,
    });
  });
}, [friends, presence]);

useEffect(() => {
  try {
    const enabled = CONTACT_PRESENCE_DIAGNOSTICS || contactPresenceRuntimeDiagnosticsEnabled();
    if (!enabled) return undefined;
    window.__z10_contacts_presence_debug__ = {
      ownerId: normalizePresenceUserId(readSelfId()),
      socketId: ioSocket?.id || null,
      authVersion: Number(ioSocket?.__z10_auth_version || 0),
      authGeneration: committedPresenceAuthGeneration(),
      sessionGeneration: authSessionGenerationRef.current,
      initializedSessionKey: initializedPresenceSessionKeyRef.current,
      cacheSessionKey: presenceCacheSessionRef.current,
      rootIdentity: rootConfirmedIdentityRef.current,
      friends,
      presence,
      lastMutation: lastPresenceMutationSourceRef.current,
    };
    return () => {
      try {
        if (window.__z10_contacts_presence_debug__?.presence === presence) {
          delete window.__z10_contacts_presence_debug__;
        }
      } catch {}
    };
  } catch {
    return undefined;
  }
}, [friends, presence]);
  
  // unread map persisted per-tab: { [friendId]: { count: number, lastText?: string, lastAt?: number } }
  const [unread, setUnread] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('csb_unread') || '{}'); } catch { return {}; }
  });

  // last activity per contact (even if messages were read)
  const [lastActivity, setLastActivity] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('csb_lastActivity') || '{}'); } catch { return {}; }
  });

  // Persist lastActivity (resistente a recargas de la pestaña)
  useEffect(() => {
    try { sessionStorage.setItem('csb_lastActivity', JSON.stringify(lastActivity || {})); } catch {}
  }, [lastActivity]);

  // === UI order: most recently chatted first ===
  // Uses unread[friendId].lastAt when available
  const friendsSorted = useMemo(() => {
    const list = Array.isArray(friends) ? friends : [];
    const withIdx = list.map((f, idx) => ({ f, idx }));

    withIdx.sort((a, b) => {
      const aId = a.f?.friendId ? String(a.f.friendId) : '';
      const bId = b.f?.friendId ? String(b.f.friendId) : '';

      const aAt =
        (aId && typeof lastActivity[aId] === 'number' ? lastActivity[aId] : 0) ||
        (aId && unread[aId] && typeof unread[aId].lastAt === 'number' ? unread[aId].lastAt : 0) ||
        0;

      const bAt =
        (bId && typeof lastActivity[bId] === 'number' ? lastActivity[bId] : 0) ||
        (bId && unread[bId] && typeof unread[bId].lastAt === 'number' ? unread[bId].lastAt : 0) ||
        0;

      if (bAt !== aAt) return bAt - aAt;
      return a.idx - b.idx;
    });

    return withIdx.map((x) => x.f);
  }, [friends, unread, lastActivity]);

  // --- Protección multi-cuenta en la MISMA pestaña ---
  const myUid = readSelfId();
    useEffect(() => {
    try {
      const storedFor = sessionStorage.getItem('csb_user');
      if (!myUid) return;
      if (storedFor && storedFor !== String(myUid)) {
        // Cambió el usuario logueado en esta pestaña:
        // limpiamos caches por contacto para que no "herede" notificaciones viejas
        sessionStorage.removeItem('csb_unread');
        sessionStorage.removeItem('csb_lastread');
        sessionStorage.removeItem('activeConvPeerId');
        clearPresenceCacheStorage();
        sessionStorage.removeItem('csb_lastActivity');
        // Rotar el tabId cuando cambia la cuenta en la misma pestaña.
        // Evita que el backend interprete el nuevo login como continuidad del socket anterior.
        sessionStorage.removeItem('z10_tab_id');
      }
      sessionStorage.setItem('csb_user', String(myUid));
    } catch {}
  }, [myUid]);

  const persistUnread = useCallback((map) => {
    try {
      sessionStorage.setItem('csb_unread', JSON.stringify(map || {}));
    } catch {}
  }, []);
  const incUnread = useCallback((senderId, { text, at } = {}) => {
    if (!senderId) return;
    setUnread((prev) => {
      const id = String(senderId);
      const cur = prev[id] || { count: 0 };
      const next = {
        ...prev,
        [id]: {
          count: (cur.count || 0) + 1,
          lastText: text || cur.lastText,
          lastAt: typeof at === 'number' ? at : (cur.lastAt || Date.now()),
        },
      };
      persistUnread(next);
      const ts = typeof at === 'number' ? at : Date.now();
      setLastActivity((prevAct) => {
        const nextAct = { ...prevAct, [id]: ts };
        try { sessionStorage.setItem('csb_lastActivity', JSON.stringify(nextAct || {})); } catch {}
        return nextAct;
      });
      return next;
    });
  }, [persistUnread]);
  // === Helpers para last read y active peer ===
  const getLastRead = useCallback((fid) => {
    try {
      const raw = sessionStorage.getItem('csb_lastread') || '{}';
      const map = JSON.parse(raw);
      return map[String(fid)] || 0;
    } catch {
      return 0;
    }
  }, []);

  const getActivePeer = useCallback(() => {
    try {
      return sessionStorage.getItem('activeConvPeerId') || null;
    } catch {
      return null;
    }
  }, []);

  const setLastRead = useCallback((fid, ts = Date.now()) => {
    try {
      const raw = sessionStorage.getItem('csb_lastread') || '{}';
      const map = JSON.parse(raw);
      map[String(fid)] = Number(ts);
      sessionStorage.setItem('csb_lastread', JSON.stringify(map));
    } catch {}
  }, []);

  const setActivePeer = useCallback((fid) => {
    try {
      if (fid) sessionStorage.setItem('activeConvPeerId', String(fid));
    } catch {}
  }, []);

  const clearUnread = useCallback((friendId) => {
    if (!friendId) return;
    setUnread((prev) => {
      if (!prev || !prev[friendId]) return prev;
      const next = { ...prev };
      delete next[friendId];
      persistUnread(next);
      return next;
    });
  }, [persistUnread]);

  // Al entrar a la lista de contactos, asumimos que la última conversación abierta quedó leída
  // y evitamos que rebote un badge viejo. También reseteamos el activePeer.
  
useEffect(() => {
  const ap = getActivePeer();
  if (ap) {
    const nowTs = Date.now();
    setLastRead(ap, nowTs);

    setUnread((prev) => {
      if (!prev || !prev[ap]) return prev;
      const next = { ...prev };
      delete next[ap];
      persistUnread(next);
      return next;
    });

    try { sessionStorage.removeItem('activeConvPeerId'); } catch {}
  }
}, [getActivePeer, setLastRead, persistUnread]);


  // Persist unread on change (resistente a recargas de la pestaña)
  useEffect(() => { persistUnread(unread); }, [unread, persistUnread]);

  // Guard extra: si por timing alguna badge re-aparece pero ya fue leída, limpiarla
  useEffect(() => {
    // Ejecutamos el barrido cuando termina la carga inicial o cambia el estado de error
    if (loading) return;
    setUnread((prev) => {
      if (!prev) return prev;
      let changed = false;
      let last = {};
      try { last = JSON.parse(sessionStorage.getItem('csb_lastread') || '{}'); } catch { last = {}; }
      const next = { ...prev };
      Object.keys(next).forEach((fid) => {
        const lr = Number(last[String(fid)] || 0);
        const la = Number(next[fid]?.lastAt || 0);
        if (lr && la && la <= lr) {
          delete next[fid];
          changed = true;
        }
      });
      if (changed) {
        try { sessionStorage.setItem('csb_unread', JSON.stringify(next)); } catch {}
        return next;
      }
      return prev;
    });
  }, [loading, err]);

  const navigate = useNavigate();
  const token = getAuthToken() || null;
  const hasToken = Boolean(token);
  const hasAuthenticatedSession = Boolean(hasToken && myUid);
  const didMountLogRef = useRef(false);
  const didConnectLogRef = useRef(false);
  const isDev = process.env.NODE_ENV === 'development';
  const reconnectCountRef = useRef(0);
    const socketRunRef = useRef(0);
  const activeUserRef = useRef(myUid ? String(myUid) : null);
  useEffect(() => {
    activeUserRef.current = myUid ? String(myUid) : null;
  }, [myUid]);

  // Owner changes are observable synchronously from auth storage. The socket
  // generation is not: it may be populated after the component's first
  // authenticated render. Never treat that metadata stabilization as a second
  // React auth session and erase an already-applied authoritative snapshot.
  // The confirmed-identity handler below commits the complete session key.
  useEffect(() => {
    const nextOwner = hasAuthenticatedSession ? normalizePresenceUserId(myUid) : null;
    if (authenticatedPresenceOwnerRef.current === nextOwner) return;

    const previousOwner = authenticatedPresenceOwnerRef.current;
    authenticatedPresenceOwnerRef.current = nextOwner;
    if (previousOwner && previousOwner !== nextOwner) clearPresenceCacheStorage();
    initializedPresenceSessionKeyRef.current = null;
    authoritativeSnapshotSessionKeyRef.current = null;
    presenceCacheSessionRef.current = null;
    presenceStateRef.current = {};
    mutatePresence('auth-owner-changed', {}, {
      reason: nextOwner ? 'authenticated-owner-changed' : 'authenticated-owner-cleared',
      previousOwner,
      nextOwner,
    });
    authSessionGenerationRef.current += 1;
    const sessionGeneration = authSessionGenerationRef.current;
    authSessionActiveRef.current = hasAuthenticatedSession;
    rootConfirmedIdentityRef.current = null;
    traceContactPresence('auth:session-changed', {
      sessionGeneration,
      myUid: myUid ? String(myUid) : null,
      tokenExists: hasToken,
      activeUserRef: activeUserRef.current,
      authSessionActive: authSessionActiveRef.current,
      socketConnected: Boolean(ioSocket?.connected),
      socketId: ioSocket?.id || null,
    });
    lastSnapAt.current = 0;
    invalidateSnapshotCoordinator();
    // Identity confirmation is the deterministic synchronization boundary.
    // Do not dispatch a speculative snapshot from partially initialized auth.
  }, [myUid, hasToken, hasAuthenticatedSession, invalidateSnapshotCoordinator, mutatePresence]);

  // ---- Sync unread desde backend (global, multi-dispositivo) ----
  const fetchConversationsSnapshot = useCallback(async () => {
    if (!token) return;
    let myIdSafe = null;
    try { myIdSafe = readSelfId(); } catch {}
    try {
      const data = await authenticatedRequest('/api/chat/conversations');
      if (!Array.isArray(data)) return;

      const nowTs = Date.now();

      // vamos a reconstruir unread limpio en base a lo que diga el server
      const rebuiltUnread = {};
      // y también vamos a reconciliar csb_lastread para pares donde el server dice "todo leído"
      let lastReadMap = {};
      try {
        lastReadMap = JSON.parse(sessionStorage.getItem('csb_lastread') || '{}');
      } catch { lastReadMap = {}; }

      for (const c of data) {
        const mems = Array.isArray(c.members) ? c.members.map(String) : [];
        // sacar el id del otro usuario
        const otherId = mems.find((m) => !myIdSafe || String(m) !== String(myIdSafe)) || mems[0] || null;
        if (!otherId) continue;

        const unreadCountServer = Number(c.unreadCount || 0);

        if (unreadCountServer > 0) {
          // dejamos badge
          const cur = rebuiltUnread[otherId] || { count: 0 };
          rebuiltUnread[otherId] = {
            ...cur,
            count: unreadCountServer,
            // no siempre tenemos preview acá; lo completamos luego con socket/fallback
            lastText: cur.lastText,
            lastAt: cur.lastAt,
          };
        } else {
          // server dice que está todo leído, aseguramos lastread alto para que no reviva
          const prevLR = Number(lastReadMap[otherId] || 0);
          if (nowTs > prevLR) {
            lastReadMap[otherId] = nowTs;
          }
        }
      }

      // persistir csb_lastread reconciliado
      try {
        sessionStorage.setItem('csb_lastread', JSON.stringify(lastReadMap));
      } catch {}

      // persistir unread global
      try {
        sessionStorage.setItem('csb_unread', JSON.stringify(rebuiltUnread));
      } catch {}

      // actualizar estado React
      setUnread(rebuiltUnread);
    } catch (e) {
      // silencio
    }
  }, [token]);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.innerWidth <= 600) {
        const center = () => {
          const el = cardRef.current;
          if (!el) return;
          const r = el.getBoundingClientRect();
          // Queremos centrar verticalmente la card en el viewport
          const target = Math.max(0, window.scrollY + r.top + (r.height / 2) - (window.innerHeight / 2));
          window.scrollTo({ top: target, left: 0, behavior: 'auto' });
        };
        // Varias pasadas para cubrir cambios de layout / barras del navegador móvil
        center();
        requestAnimationFrame(center);
        setTimeout(center, 120);
        setTimeout(center, 360);
        window.addEventListener('load', center, { once: true });
        return () => window.removeEventListener('load', center);
      }
    } catch {}
  }, []);
  // === Socket (tiempo real) ===

  // Carga amigos y solicitudes
  const refresh = useCallback(async () => {
    traceContactPresence('contacts:refresh-started', {
      sessionGeneration: authSessionGenerationRef.current,
      socketConnected: Boolean(ioSocket?.connected),
      socketId: ioSocket?.id || null,
    });
    setLoading(true);
    setErr('');
    try {
      const [f, p] = await Promise.all([
        getContacts(),
        getPendingRequests(),
      ]);
      setFriends(Array.isArray(f) ? f.map(normalizeUser) : []);
      traceContactPresence('contacts:friends-loaded', {
        sessionGeneration: authSessionGenerationRef.current,
        friendCount: Array.isArray(f) ? f.length : 0,
        contactIds: Array.isArray(f)
          ? f.map((friend) => normalizePresenceUserId(resolveContactIdentity(friend, readSelfId())?.id)).filter(Boolean)
          : [],
      });
      setPending(
        Array.isArray(p)
          ? p.map((r) => ({
              ...r,
              __requestId: r._id || r.id,
              fromUserNorm: normalizeUser(r.fromUser || r.user || r),
            }))
          : []
      );
    } catch (e) {
      setErr(e.message || 'No se pudieron cargar tus contactos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setErr('Sesión no encontrada. Iniciá sesión de nuevo.');
      setLoading(false);
      return;
    }
    refresh();

    // después de refrescar contactos, sincronizamos unread global del backend
    const tSync = setTimeout(() => {
      fetchConversationsSnapshot();
    }, 400);

    return () => clearTimeout(tSync);
  }, [token, refresh, fetchConversationsSnapshot]);

  // --- helper: pedir pendientes y actualizar lista ---
  const pullPending = useCallback(() => {
    return getPendingRequests()
      .then((p) =>
        setPending(
          Array.isArray(p)
            ? p.map((r) => ({
                ...r,
                __requestId: r._id || r.id,
                fromUserNorm: normalizeUser(r.fromUser || r.user || r),
              }))
            : []
        )
      )
      .catch(() => {});
  }, []);

  // Conexión Socket.IO reutilizando el singleton del proyecto
  useEffect(() => {
    // NOTE: Presence must be resolved in Contacts list itself,
    // not lazily when entering a chat.
    if (!token) return;
    const s = ioSocket;

        socketRunRef.current += 1;
    const runId = socketRunRef.current;
    const isAlive = () => runId === socketRunRef.current;
    const canUseSocket = () => {
      try {
        return !!(s && s.connected && isAlive());
      } catch {
        return false;
      }
    };

    if (!didMountLogRef.current) {
      if (isDev) { try { console.log('[ContactsSidebar] socket init', { connected: !!s?.connected, id: s?.id }); } catch {} }
      didMountLogRef.current = true;
    }

    const refreshPresenceForConnectedSocket = (reason) => {
      if (isAlive()) void syncContactsPresenceRef.current(reason);
    };

    // (opcional pero útil para diagnóstico)
    const onConnect = () => {
      traceContactPresence('socket:connect', {
        socketConnected: Boolean(s.connected),
        socketId: s.id || null,
        sessionGeneration: authSessionGenerationRef.current,
      });
      if (!didConnectLogRef.current) {
        if (isDev) { try { console.log('[ContactsSidebar] socket connected', { id: s.id, connected: s.connected }); } catch {} }
        didConnectLogRef.current = true;
      }
      // pedir pendientes
      pullPending();
    };
    const onIdentified = (payload = {}) => {
      const acknowledgedUserId = normalizePresenceUserId(payload?.userId);
      const currentUserId = normalizePresenceUserId(readSelfId());

      if (!canUseSocket()) return;
      if (!acknowledgedUserId) return;
      if (acknowledgedUserId !== currentUserId) return;

      traceContactPresence('socket:identified', {
        acknowledgedUserId,
        currentUserId,
        socketId: s.id || null,
        sessionGeneration: authSessionGenerationRef.current,
      });

    };

    const onManagerReconnect = () => {
      traceContactPresence('socket:reconnect', {
        socketConnected: Boolean(s.connected),
        socketId: s.id || null,
        sessionGeneration: authSessionGenerationRef.current,
      });
    };
    const onIdentityConfirmed = (event) => {
      const detail = event?.detail || event || {};
      const confirmedUserId = normalizePresenceUserId(detail.userId || detail.expectedUserId);
      const expectedUserId = normalizePresenceUserId(readSelfId());
      const socketId = String(detail.socketId || '');
      const authVersion = Number(detail.authVersion);
      if (!confirmedUserId || confirmedUserId !== expectedUserId || socketId !== String(s.id || '') || !Number.isFinite(authVersion) || authVersion !== Number(s.__z10_auth_version || 0)) return;
      const previous = rootConfirmedIdentityRef.current;
      const tupleChanged = !previous || previous.userId !== confirmedUserId || previous.socketId !== socketId || previous.authVersion !== authVersion;
      rootConfirmedIdentityRef.current = { userId: confirmedUserId, socketId, authVersion };
      if (tupleChanged) invalidateSnapshotCoordinator();
      const committedGeneration = committedPresenceAuthGeneration(s);
      const cacheSession = presenceCacheSessionKey(confirmedUserId, committedGeneration);
      const pendingSession = pendingPresenceSessionKey(confirmedUserId, socketId, authVersion);
      const confirmedSession = cacheSession || pendingSession;
      const initializedSession = initializedPresenceSessionKeyRef.current;
      const metadataStabilized = Boolean(
        cacheSession &&
        initializedSession === pendingSession &&
        previous?.userId === confirmedUserId &&
        previous?.socketId === socketId &&
        previous?.authVersion === authVersion
      );

      if (confirmedSession && initializedSession && initializedSession !== confirmedSession && !metadataStabilized) {
        clearPresenceCacheStorage();
        presenceCacheSessionRef.current = null;
        presenceStateRef.current = {};
        mutatePresence('confirmed-session-changed', {}, {
          reason: 'committed-auth-session-changed',
          previousSessionKey: initializedSession,
          nextSessionKey: confirmedSession,
        });
        authSessionGenerationRef.current += 1;
        authoritativeSnapshotSessionKeyRef.current = null;
        lastSnapAt.current = 0;
        invalidateSnapshotCoordinator();
      }

      if (confirmedSession) initializedPresenceSessionKeyRef.current = confirmedSession;
      authSessionActiveRef.current = Boolean(confirmedSession && authenticatedPresenceOwnerRef.current === confirmedUserId);

      // Cache hydration is a one-time pre-snapshot operation. A pending
      // identity session never hydrates generation-scoped cache, and metadata
      // stabilization never rehydrates over authoritative React state.
      if (cacheSession && !metadataStabilized && presenceCacheSessionRef.current !== cacheSession) {
        const restored = readPresenceCacheForSession(confirmedUserId, committedGeneration);
        const cacheMatches = Object.keys(restored).length > 0 ||
          presenceCacheSessionKey(
            normalizePresenceUserId(sessionStorage.getItem(PRESENCE_CACHE_OWNER_KEY)),
            Number(sessionStorage.getItem(PRESENCE_CACHE_AUTH_GENERATION_KEY))
          ) === cacheSession;
        if (!cacheMatches) clearPresenceCacheStorage();
        presenceCacheSessionRef.current = cacheSession;
        presenceStateRef.current = restored;
        mutatePresence('presence-cache-hydrated', restored, {
          reason: 'confirmed-session-cache',
          ownerId: confirmedUserId,
          cacheSession,
        });
        traceContactPresence('presence-cache:hydrated', {
          ownerId: confirmedUserId,
          authGeneration: committedGeneration,
          cacheSession,
          restoredIds: Object.keys(restored),
          cacheMatches,
        });
      } else if (metadataStabilized) {
        presenceCacheSessionRef.current = cacheSession;
        if (authoritativeSnapshotSessionKeyRef.current === initializedSession) {
          authoritativeSnapshotSessionKeyRef.current = cacheSession;
        }
        traceContactPresence('auth-session:metadata-stabilized', {
          ownerId: confirmedUserId,
          previousSessionKey: initializedSession,
          cacheSession,
          retainedIds: Object.keys(presenceStateRef.current || {}),
        });
      }
      traceContactPresence('identity:confirmed-consumed', { requesterUserId: expectedUserId, requesterSocketId: socketId, requesterAuthGeneration: authVersion, identityConfirmed: true, snapshotRequestReason: 'app-identity-confirmed' });
      if (
        !tupleChanged &&
        authoritativeSnapshotSessionKeyRef.current === initializedPresenceSessionKeyRef.current
      ) {
        traceContactPresence('presence-sync:deduplicated', {
          reason: metadataStabilized ? 'auth-metadata-stabilized' : 'duplicate-identity-confirmation',
          sessionKey: initializedPresenceSessionKeyRef.current,
          requestedIds: (friendsRef.current || []).map((friend) => normalizePresenceUserId(friend?.friendId)).filter(Boolean),
        });
        return;
      }
      refreshPresenceForConnectedSocket('app-identity-confirmed');
    };

    const onConnectError = (err) => { try { console.warn('[contacts] connect_error', err?.message || err); } catch {} };
    const onError = (err) => { try { console.warn('[contacts] error', err?.message || err); } catch {} };
    const onReconnectAttempt = (n) => {
      reconnectCountRef.current = typeof n === 'number' ? n : (reconnectCountRef.current + 1);
      if (reconnectCountRef.current % 5 === 0) {
        try { console.warn('[contacts] reconnect_attempt', reconnectCountRef.current); } catch {}
      }
    };
        const onDisconnect = (reason) => {
      const r = typeof reason === 'string' ? reason : (reason && reason.reason) || 'unknown';
      if (r === 'io server disconnect') {
        if (isDev) {
          try { console.log('[ContactsSidebar] socket replaced/restarted', { reason: r }); } catch {}
        }
      } else if (r !== 'transport close') {
        try { console.warn('[ContactsSidebar] socket disconnected', { reason: r }); } catch {}
      } else if (isDev) {
        try { console.log('[ContactsSidebar] socket disconnected', { reason: r }); } catch {}
      }
      invalidateSnapshotCoordinator();
      // No marcar contactos offline por desconexión local.
      // Mantiene snapshot anterior hasta que el servidor emita presence:user actualizado.
    };

    const onPresenceUser = (payload) => {
      const eventUserId = normalizePresenceUserId(payload?.userId);
      const observedRow = eventUserId ? (presenceStateRef.current?.[eventUserId] || {}) : {};
      const observedOrderingReason = eventUserId && payload
        ? presenceOrderingReason(observedRow, payload)
        : 'missing_user_id';
      traceContactPresence('contacts-presence-event-received', {
        event: 'contacts-presence-event-received',
        transitionId: payload?.transitionId || null,
        subjectUserId: eventUserId || null,
        status: payload?.status || null,
        revision: finitePresenceRevision(payload?.revision),
        updatedAt: Number(payload?.updatedAt || 0),
        existingStatus: observedRow?.online === true ? 'online' : 'offline',
        existingRevision: finitePresenceRevision(observedRow?.revision),
        accepted: !observedOrderingReason,
        rejectionReason: observedOrderingReason || null,
      });
      traceContactPresence('transition:received', {
        presenceUserId: eventUserId || null,
        online: typeof payload?.online === 'boolean' ? payload.online : null,
        updatedAt: Number(payload?.updatedAt || 0),
        lastSeen: Number(payload?.lastSeen || 0) || null,
        socketId: s?.id || null,
        socketConnected: Boolean(s?.connected),
        authVersion: Number(s?.__z10_auth_version || 0),
        sessionGeneration: authSessionGenerationRef.current,
        currentRow: {
          online: observedRow?.online === true,
          updatedAt: Number(observedRow?.updatedAt || 0),
        },
        contactKnown: Boolean(eventUserId && (friendsRef.current || [])
          .some((friend) => normalizePresenceUserId(friend?.friendId) === eventUserId)),
      });
      if (!payload || !eventUserId) {
        traceContactPresence('transition:rejected', {
          presenceUserId: eventUserId || null,
          online: typeof payload?.online === 'boolean' ? payload.online : null,
          updatedAt: Number(payload?.updatedAt || 0),
          rejectionReason: 'missing_user_id',
          currentRowUpdatedAt: Number(observedRow?.updatedAt || 0),
          socketId: s?.id || null,
          authVersion: Number(s?.__z10_auth_version || 0),
          sessionGeneration: authSessionGenerationRef.current,
        });
        return;
      }
      const now = Date.now();
      lastPresenceMutationSourceRef.current = {
        source: 'presence:user', reason: 'live-event', requestId: null,
        socketId: s?.id || null,
        authVersion: Number(s?.__z10_auth_version || 0),
        sessionGeneration: authSessionGenerationRef.current,
        timestamp: now,
      };
      mutatePresence('presence:user', (prev) => {
        const key = eventUserId;
        const prior = (prev && prev[key]) || {};
        const rejectionReason = presenceOrderingReason(prior, payload);
        if (rejectionReason) {
          traceContactPresence('transition:rejected', {
            userId: key,
            incomingOnline: typeof payload?.online === 'boolean' ? payload.online : null,
            incomingUpdatedAt: Number(payload?.updatedAt || 0),
            incomingRevision: finitePresenceRevision(payload?.revision),
            rejectionReason,
            previousOnline: prior?.online === true,
            previousUpdatedAt: Number(prior?.updatedAt || 0),
            previousRevision: finitePresenceRevision(prior?.revision),
            applied: false,
            socketId: s?.id || null,
            authVersion: Number(s?.__z10_auth_version || 0),
            sessionGeneration: authSessionGenerationRef.current,
          });
          return prev;
        }
        const hasOnlineFlag =
          Object.prototype.hasOwnProperty.call(payload, 'online') ||
          Object.prototype.hasOwnProperty.call(payload, 'connected');

        const incomingLastSeen = typeof payload.lastSeen === 'number' ? payload.lastSeen : null;
        const incomingDeviceCount = typeof payload.deviceCount === 'number' ? payload.deviceCount : (prior.deviceCount ?? 0);

        const onlineValue = Object.prototype.hasOwnProperty.call(payload, 'online')
          ? payload.online
          : payload.connected;

        const nextRow = applyPresenceUpdate(prior, {
          hasOnlineFlag,
          online: hasOnlineFlag ? !!onlineValue : undefined,
          lastSeen: incomingLastSeen,
          deviceCount: incomingDeviceCount,
          authoritative: hasOnlineFlag && onlineValue === false,
        }, now);
        const incomingRevision = finitePresenceRevision(payload.revision);
        const previousRevision = finitePresenceRevision(prior.revision);
        const storedRow = {
          ...(prev[key] || {}),
          ...nextRow,
          updatedAt: Number(payload.updatedAt || prior.updatedAt || 0),
          revision: incomingRevision ?? previousRevision,
          _liveReceivedAt: now,
          _authoritative: true,
        };
        traceContactPresence('transition:applied', {
          userId: key,
          previousOnline: prior?.online === true,
          nextOnline: storedRow.online === true,
          previousUpdatedAt: Number(prior?.updatedAt || 0),
          incomingUpdatedAt: Number(payload?.updatedAt || 0),
          previousRevision,
          incomingRevision,
          storedUpdatedAt: Number(storedRow.updatedAt || 0),
          applied: true,
          rejectionReason: null,
          socketId: s?.id || null,
          authVersion: Number(s?.__z10_auth_version || 0),
          sessionGeneration: authSessionGenerationRef.current,
        });
        traceContactPresence('presence-live:applied', {
          expectedUserId: normalizePresenceUserId(readSelfId()),
          confirmedUserId: normalizePresenceUserId(readSelfId()),
          sessionGeneration: authSessionGenerationRef.current,
          socketId: s?.id || null,
          authVersion: Number(s?.__z10_auth_version || 0),
          requestedIds: [key],
          reason: 'live-event',
        });

        // Ensure presence:user events update list even if friend not yet in presence map
        return {
          ...prev,
          [key]: storedRow,
        };
      }, {
        reason: 'live-event',
        incomingRows: eventUserId ? { [eventUserId]: payload } : {},
      });
    };

    // ---- resolve sender/text robustly for various payload shapes ----

    const shouldIncUnreadForSender = (senderId, ts) => {
  try {
    const activePeer = getActivePeer();
    const lastReadTs = getLastRead(senderId);
    return String(senderId) !== String(activePeer) && Number(ts) > Number(lastReadTs || 0);
  } catch {
    return false;
  }
};

    const pickSenderAndText = (payload) => {
      try {
        if (!payload) return { senderId: null, text: '', ts: Date.now() };
        // unwrap when payload is { conversationId, message, from }
        const msg = payload.message || payload;
        const rawSender =
          (payload.from != null ? payload.from : null) ??
          (msg && (msg.sender?._id || msg.sender?.id || msg.sender)) ??
          msg?.senderId ??
          msg?.from ??
          msg?.userId ??
          msg?.authorId ??
          null;

        const senderId = rawSender != null ? String(rawSender) : null;

        const text =
          (msg?.text ?? msg?.content ?? msg?.body ?? msg?.message ?? '') || '';

        const ts = (() => {
          const t = msg?.createdAt || msg?.timestamp || msg?.sentAt || payload?.ts;
          const n = t ? Number(new Date(t).getTime()) : NaN;
          return Number.isFinite(n) ? n : Date.now();
        })();

        return { senderId, text, ts };
      } catch {
        return { senderId: null, text: '', ts: Date.now() };
      }
    };

    const onNewMsg = (msg) => {
  const { senderId, text, ts } = pickSenderAndText(msg);
  if (!senderId) return;

  const myId = readSelfId();
  if (myId && String(myId) === String(senderId)) return;

  if (shouldIncUnreadForSender(senderId, ts)) {
    incUnread(senderId, { text, at: ts });
  }
};

    // Notificación fuera de sala (backend emite cuando NO estás en la room)
    const onConvNew = (payload) => {
  const { senderId, text, ts } = pickSenderAndText(payload || {});
  if (!senderId) return;

  const myId = readSelfId();
  if (myId && String(myId) === String(senderId)) return;

  if (shouldIncUnreadForSender(senderId, ts)) {
    incUnread(senderId, { text, at: ts });
  }
};

    // Badge incremental opcional (si tu server emite 'badge:update')
    const onBadgeUpdate = ({ conversationId, unreadInc, from }) => {
      try {
        const senderId = from ? String(from) : null;
        if (!senderId) return;
        // sube el contador sin sobrescribir el preview
        setUnread((prev) => {
          const cur = prev[senderId] || { count: 0 };
          const next = { ...prev, [senderId]: { ...cur, count: (cur.count || 0) + (Number(unreadInc) || 1) } };
          persistUnread(next);
          return next;
        });
      } catch {}
    };

    s.on('connect', onConnect);
    s.on('identified', onIdentified);
    s.io?.on('reconnect', onManagerReconnect);
    window.addEventListener('z10:socketIdentityConfirmed', onIdentityConfirmed);
    // Root identity may have completed before ContactsSidebar mounted.
    try { onIdentityConfirmed({ detail: window.__z10_confirmed_socket_identity__ }); } catch {}

    // Optional: ask server for lightweight previews if available (no-op if not implemented)
    const requestPreviews = (ids) => {
      try {
        const list = Array.isArray(ids) ? ids.filter(Boolean).map(String) : [];
        if (!list.length) return;
        s.timeout(2000).emit('conversations:previews', list, (resp) => {
          if (!resp || typeof resp !== 'object') return;
          // resp shape: { [friendId]: { lastText, lastAt, unread } }
          setUnread((prev) => {
            const next = { ...prev };
            Object.keys(resp).forEach((fid) => {
              const row = resp[fid] || {};
              const cur = next[fid] || { count: 0 };
              next[fid] = {
                count: typeof row.unread === 'number' ? row.unread : (cur.count || 0),
                lastText: row.lastText ?? cur.lastText,
                lastAt: typeof row.lastAt === 'number' ? row.lastAt : (cur.lastAt || undefined),
              };
            });
            persistUnread(next);
            return next;
          });
        });
      } catch {}
    };

    // Handler para reidentificar y refrescar presencia al reconectar o recuperar foco
        const handleRefocus = () => {
      try {
        if (!canUseSocket()) return;
        const latestSelf = readSelfId();
        if (!latestSelf || String(latestSelf) !== String(activeUserRef.current || '')) return;
        const fr = friendsRef.current || [];
        const ids = Array.isArray(fr) ? fr.map((f) => String(f.friendId)).filter(Boolean) : [];
        if (ids.length) {
          fetchUnreadForUsers(ids).catch(() => {});
          requestPreviews(ids);
        }
        // Ensure badges update even if the server doesn't emit realtime events
        fetchConversationsSnapshot();
      } catch {}
    };
    const onSocketPresenceRecovered = () => {
      handleRefocus();
      void syncContactsPresenceRef.current('socket-recovered');
    };

    if (typeof window !== 'undefined') window.addEventListener('z10:contacts-presence-recovered', onSocketPresenceRecovered);
    s.on('connect_error', onConnectError);
    s.on('disconnect', onDisconnect);
    s.on('error', onError);
    s.on('reconnect_attempt', onReconnectAttempt);
    s.on('presence:user', onPresenceUser);
    s.on('newConversationMessage', onNewMsg);
    s.on('conversation:new', onConvNew);
    s.on('badge:update', onBadgeUpdate);

    // Compatibility listeners (server variants)
    s.on('conversation:message', onNewMsg);
    s.on('conversation:newMessage', onNewMsg);
    s.on('dm:new', onConvNew);
    s.on('dm:message', onNewMsg);

    // === Contactos: eventos en tiempo real ===
    const onContactRequestReceived = (payload) => {
      if (isDev) { try { console.log('[contacts] request-received', payload); } catch {} }
      // Refresca solo las solicitudes pendientes
      pullPending();
    };
    const onContactAccepted = (payload) => {
      if (isDev) { try { console.log('[contacts] request-accepted', payload); } catch {} }
      // Refresca la lista completa (amigos + pendientes)
      refresh();
    };
    const onContactDeclined = (payload) => {
      if (isDev) { try { console.log('[contacts] request-declined', payload); } catch {} }
      // Solo re-chequear pendientes
      pullPending();
    };
    const onContactRemoved = (payload) => {
      if (isDev) { try { console.log('[contacts] removed', payload); } catch {} }
      const rid = (payload && (payload.by || payload.friendId || payload.userId || payload.id))
        ? String(payload.by || payload.friendId || payload.userId || payload.id)
        : null;
      if (rid) {
        setFriends((prev) => prev.filter((f) => String(f.friendId) !== rid));
      } else {
        // si no viene ID, hacemos refresh de respaldo
        refresh();
      }
    };

    s.on('contacts:request-received', onContactRequestReceived);
    s.on('contacts:request-accepted', onContactAccepted);
    s.on('contacts:request-declined', onContactDeclined);
    s.on('contacts:removed', onContactRemoved);

    // si ya está conectado (HMR/strict), forzar el flujo
    if (s.connected) onConnect();

    try {
      const fr = friendsRef.current || [];
      const ids = Array.isArray(fr) ? fr.map((f) => String(f.friendId)).filter(Boolean) : [];
      if (ids.length) requestPreviews(ids);
    } catch {}

    return () => {
      if (typeof window !== 'undefined') window.removeEventListener('z10:contacts-presence-recovered', onSocketPresenceRecovered);
      socketRunRef.current += 1;
      try {
        s.off('connect', onConnect);
        s.off('identified', onIdentified);
        s.io?.off('reconnect', onManagerReconnect);
        window.removeEventListener('z10:socketIdentityConfirmed', onIdentityConfirmed);
        s.off('connect_error', onConnectError);
        s.off('disconnect', onDisconnect);
        s.off('error', onError);
        s.off('reconnect_attempt', onReconnectAttempt);
        s.off('presence:user', onPresenceUser);
        s.off('newConversationMessage', onNewMsg);
        s.off('conversation:new', onConvNew);
        s.off('badge:update', onBadgeUpdate);
        s.off('conversation:message', onNewMsg);
        s.off('conversation:newMessage', onNewMsg);
        s.off('dm:new', onConvNew);
        s.off('dm:message', onNewMsg);
        s.off('contacts:request-received', onContactRequestReceived);
        s.off('contacts:request-accepted', onContactAccepted);
        s.off('contacts:request-declined', onContactDeclined);
        s.off('contacts:removed', onContactRemoved);
      } catch {}
    };
  }, [token, pullPending, incUnread, persistUnread, refresh, snapshotPresence, isDev, getActivePeer, fetchConversationsSnapshot, getLastRead, invalidateSnapshotCoordinator, mutatePresence]);

  // Contacts and identity are independent async inputs.  This effect merely
  // announces that contacts are ready; the single synchronizer owns ordering.
  useEffect(() => {
    traceContactPresence('contacts:friends-effect', {
      friendCount: Array.isArray(friends) ? friends.length : 0,
      contactIds: Array.isArray(friends) ? friends.map((f) => normalizePresenceUserId(f?.friendId)).filter(Boolean) : [],
      socketConnected: Boolean(ioSocket?.connected),
      socketId: ioSocket?.id || null,
      sessionGeneration: authSessionGenerationRef.current,
    });
    fetchConversationsSnapshot();
    if (Array.isArray(friends) && friends.length) void syncContactsPresenceRef.current('contacts-loaded');
  }, [friends, fetchConversationsSnapshot]);

  useEffect(() => {
    const reconcile = () => {
      try {
        const raw = sessionStorage.getItem('csb_lastread') || '{}';
        const last = JSON.parse(raw);
        setUnread((prev) => {
          if (!prev) return prev;
          const next = { ...prev };
          let changed = false;
          Object.keys(next).forEach((fid) => {
            const lr = Number(last[fid] || 0);
            const la = Number(next[fid]?.lastAt || 0);
            if (lr && la && la <= lr) {
              delete next[fid];
              changed = true;
            }
          });
          if (changed) {
            persistUnread(next);
          }
          return changed ? next : prev;
        });
      } catch {}
    };
    const onRealtimeReconciled = () => reconcile();
    window.addEventListener('z10:contacts-presence-recovered', onRealtimeReconciled);
    reconcile(); // al montar

    return () => {
      window.removeEventListener('z10:contacts-presence-recovered', onRealtimeReconciled);
    };
  }, [persistUnread]);

  // === Fallback de previews: si el server no emite eventos de preview/unread,
  // consultamos el último mensaje por contacto (capado y escalonado) ===
  useEffect(() => {
  let cancelled = false;
  const myId = readSelfId();
  const FALLBACK_TTL_MS = 60 * 1000;
  const FALLBACK_KEY = `csb_pullLatest_ts_${String(myUid || 'anon')}`;

  async function pullLatestForFriends() {
    if (!token || !Array.isArray(friends) || friends.length === 0) return;

    // Throttle per tab/user so this fallback does not hammer the backend on every mount.
    try {
      const lastRun = Number(sessionStorage.getItem(FALLBACK_KEY) || 0);
      if (lastRun && Date.now() - lastRun < FALLBACK_TTL_MS) return;
      sessionStorage.setItem(FALLBACK_KEY, String(Date.now()));
    } catch {}

    // Only run fallback for contacts that do not already have cached unread/activity info.
    const sample = friends
      .filter((f) => {
        if (!f || !f.friendId) return false;
        const fid = String(f.friendId);
        const hasUnreadMeta = !!(unread && unread[fid] && (unread[fid].lastText || unread[fid].lastAt || unread[fid].count));
        const hasActivity = !!(lastActivity && typeof lastActivity[fid] === 'number' && lastActivity[fid] > 0);
        return !hasUnreadMeta && !hasActivity;
      })
      .slice(0, 4);

    if (!sample.length) return;

    for (let i = 0; i < sample.length; i++) {
      if (cancelled) return;
      const f = sample[i];
      const fid = String(f.friendId);

      try {
        // 1) Conseguir (o crear) la conversación idempotente
        const conv = await openOrCreateConversation(undefined, fid);
        const convId = conv?._id || conv?.id;
        if (!convId) {
          continue;
        }

        // 2) Traer mensajes y quedarnos con el último
        const resp = await getConversationMessages(undefined, convId);
        let msgs = [];
        if (Array.isArray(resp)) msgs = resp;
        else if (resp && Array.isArray(resp.messages)) msgs = resp.messages;
        else if (resp && resp.data && Array.isArray(resp.data.messages)) msgs = resp.data.messages;

        if (!msgs.length) {
          continue;
        }

        const last = msgs[msgs.length - 1];
        const senderRaw =
          (last?.sender && (last.sender._id || last.sender.id)) ||
          last?.sender;
        const senderId = senderRaw ? String(senderRaw) : null;

        const tsRaw = last?.createdAt || last?.timestamp;
        const ts = tsRaw ? Number(new Date(tsRaw).getTime()) : 0;
        const text =
          last?.text ??
          last?.content ??
          last?.body ??
          last?.message ??
          '';

        // Sólo marcamos como preview si el último lo envió el OTRO usuario
        if (senderId && myId && senderId !== String(myId) && ts > 0) {
          // --- NUEVO: respetar lo que ya leí ---
          const lastReadTs = getLastRead(fid);

          // si YA leí después de ese mensaje, no lo cuentes como unread
          if (lastReadTs && ts <= lastReadTs) {
            // por si quedó badge viejo, lo limpiamos también acá
            setUnread((prev) => {
              if (!prev || !prev[fid]) return prev;
              const cleaned = { ...prev };
              delete cleaned[fid];
              try {
                sessionStorage.setItem(
                  'csb_unread',
                  JSON.stringify(cleaned)
                );
              } catch {}
              return cleaned;
            });

            continue;
          }

          // si NO está leído todavía, actualizar preview/unread de ese amigo
          setUnread((prev) => {
            const cur = prev[fid];

            // si ya tengo algo más reciente, no retroceder
            if (
              cur &&
              typeof cur.lastAt === 'number' &&
              cur.lastAt >= ts
            ) {
              return prev;
            }

            // si estoy acá, es un mensaje nuevo no leído
            const next = {
              ...prev,
              [fid]: {
                count: Math.max(1, cur?.count || 0),
                lastText: text,
                lastAt: ts,
              },
            };

            try {
              sessionStorage.setItem(
                'csb_unread',
                JSON.stringify(next)
              );
            } catch {}
            return next;
          });
        }
      } catch (e) {
        // silencio si falla uno, seguimos con el resto
      }

      // no artificial delay here; throttling + smaller sample already limits load
    }
  }

  pullLatestForFriends();

  return () => {
    cancelled = true;
  };
}, [token, friends, unread, lastActivity, setUnread, getLastRead, myUid]);

  // (Eliminado: desconectar socketRef.current en beforeunload; ya no necesario con singleton)

  // Buscar usuarios (debounce simple)
  useEffect(() => {
    const t = setTimeout(async () => {
      const q = query.trim();
      if (!q) { setResults([]); return; }
      try {
        setSearching(true);
        const list = await searchUsers(token, q);
        setResults(Array.isArray(list) ? list.map(normalizeUser) : []);
      } catch (e) {
        console.warn('search error', e.message || e);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query, token]);

  // Aceptar/Rechazar solicitudes
  const onAccept = async (requestId) => {
    try {
      await acceptRequest(undefined, requestId);
      await refresh();
      // refrescar resultados de búsqueda para actualizar flags
      if (query.trim()) {
        const fresh = await searchUsers(token, query.trim());
        setResults(Array.isArray(fresh) ? fresh.map(normalizeUser) : []);
      }
    } catch (e) {
      alert(e.message || t('contacts.errors.acceptFailed'));
    }
  };
  const onReject = async (requestId) => {
    try {
      await rejectRequest(undefined, requestId);
      await refresh();
      if (query.trim()) {
        const fresh = await searchUsers(token, query.trim());
        setResults(Array.isArray(fresh) ? fresh.map(normalizeUser) : []);
      }
    } catch (e) {
      alert(e.message || t('contacts.errors.rejectFailed'));
    }
  };

  // Abrir chat con amigo
  const onChat = async (candidateId) => {
    const friendId = candidateId ? String(candidateId).trim() : '';
    if (!/^[a-f0-9]{24}$/i.test(friendId)) {
      alert(t('contacts.errors.invalidContactId'));
      return;
    }

    try {
      // 0. timestamp "leído ahora"
      const nowTs = Date.now();

      setLastActivity((prevAct) => {
        const nextAct = { ...prevAct, [friendId]: nowTs };
        try { sessionStorage.setItem('csb_lastActivity', JSON.stringify(nextAct || {})); } catch {}
        return nextAct;
      });

      // 1. marcar leído LOCAL antes de navegar
      setLastRead(friendId, nowTs);
      clearUnread(friendId);
      setActivePeer(friendId);

      try {
        // sincronizar csb_unread sin esperar al próximo render
        const rawUnread = sessionStorage.getItem('csb_unread') || '{}';
        const mapUnread = JSON.parse(rawUnread);
        delete mapUnread[friendId];
        sessionStorage.setItem('csb_unread', JSON.stringify(mapUnread));

        // sincronizar csb_lastread también
        const rawLR = sessionStorage.getItem('csb_lastread') || '{}';
        const mapLR = JSON.parse(rawLR);
        mapLR[String(friendId)] = nowTs;
        sessionStorage.setItem('csb_lastread', JSON.stringify(mapLR));
      } catch {}

      // 2. conseguir (o crear) la conversación idempotente con ese friend
      const conv = await openOrCreateConversation(undefined, friendId);
      const convId = conv?._id || conv?.id;
      if (!convId) throw new Error('No se obtuvo la conversación');

      // 3. (Eliminado: llamada a /api/chat/${convId}/read-global y fetchConversationsSnapshot)

      // 4. navegar al chat (pasamos peerUserId para que el header resuelva presencia al instante)
      navigate(`/chat/${convId}`, { state: { peerUserId: friendId } });
    } catch (e) {
      alert(e.message || t('contacts.errors.openChatFailed'));
    }
  };
  // Eliminar amigo
  const onRemove = async (friendId) => {
    if (!friendId) return;
    if (!window.confirm(t('contacts.confirmRemove'))) return;
    try {
      await removeContact(undefined, friendId);
      setFriends((prev) => prev.filter((f) => f.friendId !== String(friendId)));
    } catch (e) {
      alert(e.message || t('contacts.errors.removeFailed'));
    }
  };

// ===== estilos =====
const S = {
  page: {
    minHeight: '100dvh',
    background: 'transparent',
    padding: 'calc(env(safe-area-inset-top, 0px) + 18px) 14px calc(env(safe-area-inset-bottom, 0px) + 22px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    position: 'relative',
    isolation: 'isolate',
    overflowX: 'hidden',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    color: '#F8F1FF',
    boxSizing: 'border-box',
  },

  card: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: 620,
    background: 'linear-gradient(180deg, rgba(28,7,58,0.88) 0%, rgba(12,0,31,0.92) 100%)',
    borderRadius: 32,
    overflow: 'hidden',
    boxShadow: '0 28px 78px rgba(0,0,0,0.44), 0 0 42px rgba(170,64,255,0.16), inset 0 1px 0 rgba(255,255,255,0.08)',
    border: '1px solid rgba(218,165,32,0.26)',
    backdropFilter: 'blur(16px) saturate(1.12)',
    WebkitBackdropFilter: 'blur(16px) saturate(1.12)',
    boxSizing: 'border-box',
  },

  header: {
    display: 'grid',
    gridTemplateColumns: '86px minmax(0, 1fr)',
    alignItems: 'center',
    gap: 14,
    padding: '22px 20px 20px',
    background: 'linear-gradient(135deg, rgba(75,0,130,0.92) 0%, rgba(28,2,62,0.92) 58%, rgba(10,0,24,0.96) 100%)',
    color: '#ffffff',
    borderBottom: '1px solid rgba(218,165,32,0.18)',
    minHeight: 112,
    boxSizing: 'border-box',
  },
  titleWrap: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  title: {
    margin: 0,
    fontSize: 'clamp(1.62rem, 6.8vw, 2.25rem)',
    fontWeight: 950,
    letterSpacing: '-0.04em',
    color: '#FFFFFF',
    lineHeight: 0.98,
    textShadow: '0 0 22px rgba(255,255,255,0.10)',
  },
  subtitle: {
    margin: '7px 0 0',
    fontSize: 'clamp(0.78rem, 3.2vw, 0.96rem)',
    opacity: 0.76,
    color: 'rgba(248,241,255,0.78)',
    lineHeight: 1.25,
  },

  backBtn: {
    border: '1px solid rgba(255,255,255,0.16)',
    background: 'linear-gradient(145deg, rgba(255,255,255,0.12), rgba(138,43,226,0.14))',
    color: '#FFF6C8',
    padding: '11px 13px',
    borderRadius: 18,
    cursor: 'pointer',
    fontWeight: 900,
    boxShadow: '0 12px 26px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.10)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    minHeight: 54,
    minWidth: 74,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    textShadow: '0 2px 10px rgba(0,0,0,0.35)',
  },

  body: {
    padding: '20px 16px 24px',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 24,
    background: 'linear-gradient(180deg, rgba(17,0,42,0.40), rgba(5,0,14,0.18))',
    overflow: 'hidden',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100%',
  },

  sectionTitle: {
    margin: '0 0 12px',
    color: '#F3C96B',
    textShadow: '0 0 18px rgba(218,165,32,0.18)',
    fontWeight: 950,
    fontSize: '0.82rem',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
  },

  searchBox: {
    display: 'flex',
    gap: 10,
    background: 'linear-gradient(145deg, rgba(255,255,255,0.10), rgba(138,43,226,0.13))',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 22,
    padding: 8,
    boxShadow: '0 18px 42px rgba(0,0,0,0.26), inset 0 1px 0 rgba(255,255,255,0.08)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  input: {
    flex: 1,
    padding: '14px 15px',
    borderRadius: 18,
    border: '1px solid rgba(255,255,255,0.10)',
    outline: 'none',
    background: 'rgba(7,0,22,0.46)',
    fontSize: 16,
    color: '#FFFFFF',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
    caretColor: '#F3C96B',
    minWidth: 0,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },

  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    margin: 0,
    padding: 0,
    listStyle: 'none',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: 13,
    borderRadius: 24,
    background: 'linear-gradient(145deg, rgba(255,255,255,0.075), rgba(138,43,226,0.11))',
    border: '1px solid rgba(255,255,255,0.10)',
    boxShadow: '0 18px 42px rgba(0,0,0,0.26), inset 0 1px 0 rgba(255,255,255,0.06)',
    flexWrap: 'wrap',
    backdropFilter: 'blur(13px) saturate(1.08)',
    WebkitBackdropFilter: 'blur(13px) saturate(1.08)',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },

  userBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: 13,
    minWidth: 0,
    flex: '1 1 100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  avatar: {
    width: 62,
    height: 62,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #DAA520',
    background: '#12002E',
    boxShadow: '0 10px 24px rgba(0,0,0,0.28), 0 0 18px rgba(218,165,32,0.22)',
    flex: '0 0 auto',
  },
  userTxt: { display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 },
  name: {
    fontWeight: 950,
    color: '#FFFFFF',
    fontSize: '1.05rem',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: '100%',
    letterSpacing: '-0.02em',
  },
  metaRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' },
  bio: {
    fontSize: '0.86rem',
    color: 'rgba(248,241,255,0.68)',
    marginTop: 6,
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  chip: {
    fontSize: 12,
    padding: '5px 12px',
    borderRadius: 999,
    background: 'linear-gradient(145deg, rgba(138,43,226,0.28), rgba(255,255,255,0.07))',
    color: '#E9C2FF',
    border: '1px solid rgba(230,170,255,0.30)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
  },
  idHint: { fontSize: 10, color: 'rgba(248,241,255,0.48)' },

  btn: {
    padding: '11px 14px',
    borderRadius: 16,
    border: '1px solid rgba(218,165,32,0.50)',
    background: 'rgba(255,255,255,0.03)',
    cursor: 'pointer',
    fontWeight: 900,
    color: '#F3C96B',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
    minWidth: 'min(46%, 170px)',
    flex: '1 1 46%',
  },
  btnPrimary: {
    padding: '11px 14px',
    borderRadius: 16,
    border: 'none',
    background: 'linear-gradient(135deg, #DAA520, #F7D774)',
    color: '#2B0048',
    fontWeight: 950,
    cursor: 'pointer',
    boxShadow: '0 12px 24px rgba(218,165,32,0.26), inset 0 1px 0 rgba(255,255,255,0.34)',
    minWidth: 'min(46%, 170px)',
    flex: '1 1 46%',
  },
  btnDanger: {
    padding: '11px 14px',
    borderRadius: 16,
    border: '1px solid rgba(255,105,135,0.56)',
    background: 'linear-gradient(135deg, rgba(255,77,96,0.96), rgba(255,71,116,0.88))',
    color: '#ffffff',
    cursor: 'pointer',
    fontWeight: 900,
    boxShadow: '0 12px 24px rgba(255,71,116,0.20), inset 0 1px 0 rgba(255,255,255,0.22)',
    minWidth: 'min(46%, 170px)',
    flex: '1 1 46%',
  },

  empty: {
    padding: '22px 16px',
    color: 'rgba(248,241,255,0.78)',
    fontStyle: 'normal',
    background: 'linear-gradient(145deg, rgba(255,255,255,0.07), rgba(138,43,226,0.10))',
    borderRadius: 22,
    border: '1px solid rgba(255,255,255,0.10)',
    textAlign: 'center',
    boxShadow: '0 18px 42px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.06)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
  },
};

  if (loading) {
    return (
      <div id="contactsRoot" style={S.page}>
        <style>{`
          @keyframes wheel-spin {
            from { transform: translate(-50%, -50%) rotate(0deg); }
            to { transform: translate(-50%, -50%) rotate(360deg); }
          }
          /* Mobile: centrar vertical y evitar que arranque “arriba/abajo” usando el viewport dinámico */
          @media (max-width: 600px) {
            #contactsRoot { 
              min-height: 100dvh !important; 
              width: 100vw !important;
              max-width: 100vw !important;
              display: flex; 
              align-items: flex-start !important; 
              justify-content: center; 
              padding: calc(env(safe-area-inset-top, 0px) + 12px) 10px calc(env(safe-area-inset-bottom, 0px) + 16px) !important;
              overflow-x: hidden !important;
              overflow-y: auto !important;
              background: #070014 !important;
              box-sizing: border-box !important;
            }
            #contactsRoot .contacts-card {
              width: calc(100vw - 20px) !important;
              max-width: calc(100vw - 20px) !important;
              margin: 0 auto !important;
              border-radius: 28px !important;
              overflow: hidden !important;
              box-sizing: border-box !important;
            }
            #contactsRoot .contactsBackBtn {
              margin-top: 0 !important;
              min-width: 70px !important;
            }
            #contactsRoot > div,
            #contactsRoot .contacts-card > div,
            #contactsRoot section,
            #contactsRoot ul,
            #contactsRoot li,
            #contactsRoot form,
            #contactsRoot input {
              min-width: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              box-sizing: border-box !important;
            }
            #contactsRoot input {
              display: block !important;
            }
            /* iOS Safari zoom fix: prevent auto-zoom on input focus by ensuring >=16px font-size */
            #contactsRoot input,
            #contactsRoot textarea,
            #contactsRoot select {
              font-size: 16px !important;
            }
            #searchPeople {
              font-size: 16px !important;
            }
          }
        `}</style>
        {/* Fondo base violeta uniforme (sin segunda ruleta ni blur) */}
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 0,
            background: 'radial-gradient(circle at 18% 12%, rgba(255,215,0,0.13), transparent 24%), radial-gradient(circle at 84% 18%, rgba(190,80,255,0.22), transparent 30%), radial-gradient(circle at 50% 70%, rgba(75,0,130,0.44), transparent 55%), linear-gradient(180deg, #070014 0%, #130027 44%, #050008 100%)',
            pointerEvents: 'none',
          }}
        />
        {/* Spinning zodiac wheel */}
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            width: 'min(96svmin, 1200px)',
            height: 'min(96svmin, 1200px)',
            transform: 'translate(-50%, -50%)',
            backgroundImage: 'url("/logo/Zodiacus.jpeg")',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            borderRadius: '50%',
            animation: 'wheel-spin 140s linear infinite',
            zIndex: 1,
            pointerEvents: 'none',
            opacity: 0.96,
            boxShadow: '0 0 60px rgba(0,0,0,0.18), inset 0 0 10px rgba(0,0,0,0.12)',
            willChange: 'transform',
            transformOrigin: 'center center',
            filter: 'brightness(1.08) contrast(1.12) saturate(1.08)',
          }}
        />
        <div ref={cardRef} style={S.card} className="contacts-card">
          <div style={S.header}>
            <button className="contactsBackBtn" style={S.backBtn} onClick={() => navigate(-1)}>← {t('actions.back')}</button>
            <div style={S.titleWrap}>
              <h2 style={S.title}>{t('contacts.title')}</h2>
              <p style={S.subtitle}>{t('states.loading')}</p>
            </div>
          </div>
          <div style={{ padding: 16 }}>
            <div style={S.empty}>{t('contacts.states.loading')}</div>
          </div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div id="contactsRoot" style={S.page}>
        <style>{`
          @keyframes wheel-spin {
            from { transform: translate(-50%, -50%) rotate(0deg); }
            to { transform: translate(-50%, -50%) rotate(360deg); }
          }
          /* Mobile: centrar vertical y evitar que arranque “arriba/abajo” usando el viewport dinámico */
          @media (max-width: 600px) {
            #contactsRoot { 
              min-height: 100dvh !important; 
              width: 100vw !important;
              max-width: 100vw !important;
              display: flex; 
              align-items: flex-start !important; 
              justify-content: center; 
              padding: calc(env(safe-area-inset-top, 0px) + 12px) 10px calc(env(safe-area-inset-bottom, 0px) + 16px) !important;
              overflow-x: hidden !important;
              overflow-y: auto !important;
              background: #070014 !important;
              box-sizing: border-box !important;
            }
            #contactsRoot .contacts-card {
              width: calc(100vw - 20px) !important;
              max-width: calc(100vw - 20px) !important;
              margin: 0 auto !important;
              border-radius: 28px !important;
              overflow: hidden !important;
              box-sizing: border-box !important;
            }
            #contactsRoot .contactsBackBtn {
              margin-top: 0 !important;
              min-width: 70px !important;
            }
            #contactsRoot > div,
            #contactsRoot .contacts-card > div,
            #contactsRoot section,
            #contactsRoot ul,
            #contactsRoot li,
            #contactsRoot form,
            #contactsRoot input {
              min-width: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              box-sizing: border-box !important;
            }
            #contactsRoot input {
              display: block !important;
            }
            /* iOS Safari zoom fix: prevent auto-zoom on input focus by ensuring >=16px font-size */
            #contactsRoot input,
            #contactsRoot textarea,
            #contactsRoot select {
              font-size: 16px !important;
            }
            #searchPeople {
              font-size: 16px !important;
            }
          }
        `}</style>
        {/* Fondo base violeta uniforme (sin segunda ruleta ni blur) */}
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 0,
            background: 'radial-gradient(circle at 18% 12%, rgba(255,215,0,0.13), transparent 24%), radial-gradient(circle at 84% 18%, rgba(190,80,255,0.22), transparent 30%), radial-gradient(circle at 50% 70%, rgba(75,0,130,0.44), transparent 55%), linear-gradient(180deg, #070014 0%, #130027 44%, #050008 100%)',
            pointerEvents: 'none',
          }}
        />
        {/* Spinning zodiac wheel */}
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            width: 'min(96svmin, 1200px)',
            height: 'min(96svmin, 1200px)',
            transform: 'translate(-50%, -50%)',
            backgroundImage: 'url("/logo/Zodiacus.jpeg")',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            borderRadius: '50%',
            animation: 'wheel-spin 140s linear infinite',
            zIndex: 1,
            pointerEvents: 'none',
            opacity: 0.94,
            boxShadow: '0 0 60px rgba(0,0,0,0.18), inset 0 0 10px rgba(0,0,0,0.12)',
            willChange: 'transform',
            transformOrigin: 'center center',
            filter: 'brightness(1.08) contrast(1.12) saturate(1.08)',
          }}
        />
        <div ref={cardRef} style={S.card} className="contacts-card">
          <div style={S.header}>
            <button className="contactsBackBtn" style={S.backBtn} onClick={() => navigate(-1)}>← {t('actions.back')}</button>
            <div style={S.titleWrap}>
              <h2 style={S.title}>{t('contacts.title')}</h2>
              <p style={S.subtitle}>{t('states.error')}</p>
            </div>
          </div>
          <div style={{ padding: 16 }}>
            <div style={{ ...S.empty, color: 'crimson', borderColor: '#f4b4b4' }}>{err}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="contactsRoot" style={S.page}>
      <style>{`
          @keyframes wheel-spin {
            from { transform: translate(-50%, -50%) rotate(0deg); }
            to { transform: translate(-50%, -50%) rotate(360deg); }
          }
          /* Mobile: centrar vertical y evitar que arranque “arriba/abajo” usando el viewport dinámico */
          @media (max-width: 600px) {
            #contactsRoot { 
              min-height: 100dvh !important; 
              width: 100vw !important;
              max-width: 100vw !important;
              display: flex; 
              align-items: flex-start !important; 
              justify-content: center; 
              padding: calc(env(safe-area-inset-top, 0px) + 12px) 10px calc(env(safe-area-inset-bottom, 0px) + 16px) !important;
              overflow-x: hidden !important;
              overflow-y: auto !important;
              background: #070014 !important;
              box-sizing: border-box !important;
            }
            #contactsRoot .contacts-card {
              width: calc(100vw - 20px) !important;
              max-width: calc(100vw - 20px) !important;
              margin: 0 auto !important;
              border-radius: 28px !important;
              overflow: hidden !important;
              box-sizing: border-box !important;
            }
            #contactsRoot .contactsBackBtn {
              margin-top: 0 !important;
              min-width: 70px !important;
            }
            #contactsRoot > div,
            #contactsRoot .contacts-card > div,
            #contactsRoot section,
            #contactsRoot ul,
            #contactsRoot li,
            #contactsRoot form,
            #contactsRoot input {
              min-width: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              box-sizing: border-box !important;
            }
            #contactsRoot input {
              display: block !important;
            }
            /* iOS Safari zoom fix: prevent auto-zoom on input focus by ensuring >=16px font-size */
            #contactsRoot input,
            #contactsRoot textarea,
            #contactsRoot select {
              font-size: 16px !important;
            }
            #searchPeople {
              font-size: 16px !important;
            }
          }
        `}</style>
        {/* Fondo base violeta uniforme (sin segunda ruleta ni blur) */}
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 0,
            background: 'radial-gradient(circle at 18% 12%, rgba(255,215,0,0.13), transparent 24%), radial-gradient(circle at 84% 18%, rgba(190,80,255,0.22), transparent 30%), radial-gradient(circle at 50% 70%, rgba(75,0,130,0.44), transparent 55%), linear-gradient(180deg, #070014 0%, #130027 44%, #050008 100%)',
            pointerEvents: 'none',
          }}
        />
        {/* Spinning zodiac wheel */}
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            width: 'min(96svmin, 1200px)',
            height: 'min(96svmin, 1200px)',
            transform: 'translate(-50%, -50%)',
            backgroundImage: 'url("/logo/Zodiacus.jpeg")',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            borderRadius: '50%',
            animation: 'wheel-spin 140s linear infinite',
            zIndex: 1,
            pointerEvents: 'none',
            opacity: 0.94,
            boxShadow: '0 0 60px rgba(0,0,0,0.18), inset 0 0 10px rgba(0,0,0,0.12)',
            willChange: 'transform',
            transformOrigin: 'center center',
            filter: 'brightness(1.08) contrast(1.12) saturate(1.08)',
          }}
        />
      <div ref={cardRef} style={S.card} className="contacts-card">
        <div style={S.header}>
          <button className="contactsBackBtn" style={S.backBtn} onClick={() => navigate('/home')}>← {t('actions.back')}</button>
          <div style={S.titleWrap}>
            <h2 style={S.title}>{t('contacts.title')}</h2>
            <p style={S.subtitle}>{t('contacts.subtitle')}</p>
          </div>
        </div>

        <div style={S.body}>
          {/* Buscar usuarios */}
          <section>
            <div style={S.sectionTitle}>{t('contacts.search.title')}</div>
            <div style={S.searchBox}>
              <input
                id="searchPeople"
                name="searchPeople"
                autoComplete="off"
                aria-label={t('contacts.search.ariaLabel')}
                style={S.input}
                placeholder={t('contacts.search.placeholder')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            {searching && <div style={S.empty}>{t('contacts.states.searching')}</div>}

            {!searching && query.trim() && results.length === 0 && (
              <div style={S.empty}>{t('contacts.search.empty')}</div>
            )}

            {!searching && results.length > 0 && (
              <ul style={S.list}>
                {results.map((u) => (
                  <li key={u._id} style={S.item}>
                    <div style={S.userBlock}>
                      <img
                        src={contactPhotoSrc(u)}
                        alt={u.fullName}
                        style={S.avatar}
                        onError={(e) => {
                          if (e.currentTarget.src.includes('logo192.png')) return;
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '/logo192.png';
                        }}
                      />
                      <div style={S.userTxt}>
                        <div style={S.name} title={u.fullName}>{u.fullName}</div>
                        <div style={S.metaRow}>
                          {u.zodiacSign && <span style={S.chip}>{u.zodiacSign}</span>}
                        </div>
                        {u.bio && <div style={S.bio}>{u.bio}</div>}
                      </div>
                    </div>

                    {u.isFriend ? (
                      <button
                        style={{ ...S.btn, cursor: 'default', opacity: 0.7 }}
                        disabled
                      >
                        {t('contacts.relationship.friends')}
                      </button>
                    ) : u.isPending ? (
                      <button
                        style={{ ...S.btn, cursor: 'default', opacity: 0.7 }}
                        disabled
                      >
                        {t('contacts.relationship.pending')}
                      </button>
                    ) : (
                      <button
                        style={S.btnPrimary}
                        onClick={async () => {
                          try {
                            const res = await sendRequest(undefined, u._id);
                            if (res?.conflict) {
                              alert(res.message || t('contacts.notices.alreadyContacts'));
                            } else {
                              alert(t('contacts.notices.requestSent'));
                            }
                            // refrescar amigos/pendientes y flags en búsqueda
                            await refresh();
                            if (query.trim()) {
                              const fresh = await searchUsers(token, query.trim());
                              setResults(Array.isArray(fresh) ? fresh.map(normalizeUser) : []);
                            }
                          } catch (er) {
                            alert(er.message || t('contacts.errors.sendRequestFailed'));
                          }
                        }}
                      >
                        {t('contacts.actions.add')}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Solicitudes pendientes */}
          <section>
            <div style={S.sectionTitle}>{t('contacts.requests.title')} {pending?.length ? `(${pending.length})` : ''}</div>
            {pending.length === 0 ? (
              <div style={S.empty}>{t('contacts.requests.empty')}</div>
            ) : (
              <ul style={S.list}>
                {pending.map((r) => (
                  <li key={r.__requestId} style={S.item}>
                    <div style={S.userBlock}>
                      <img
                        src={contactPhotoSrc(r.fromUserNorm)}
                        alt={r.fromUserNorm.fullName || t('contacts.userFallback')}
                        style={S.avatar}
                        onError={(e) => {
                          if (e.currentTarget.src.includes('logo192.png')) return;
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '/logo192.png';
                        }}
                      />
                      <div style={S.userTxt}>
                        <div style={S.name} title={r.fromUserNorm.fullName}>{r.fromUserNorm.fullName}</div>
                        <div style={S.metaRow}>
                          {r.fromUserNorm.zodiacSign && <span style={S.chip}>{r.fromUserNorm.zodiacSign}</span>}
                        </div>
                        {r.fromUserNorm.bio && <div style={S.bio}>{r.fromUserNorm.bio}</div>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button style={S.btnPrimary} onClick={() => onAccept(r.__requestId)}>{t('contacts.actions.accept')}</button>
                      <button style={S.btnDanger} onClick={() => onReject(r.__requestId)}>{t('contacts.actions.reject')}</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Lista de amigos */}
          <section>
            <div style={S.sectionTitle}>{t('contacts.list.title')}</div>
            {friends.length === 0 ? (
              <div style={S.empty}>{t('contacts.list.empty')}</div>
            ) : (
              <ul style={S.list}>
                {friendsSorted.map((u) => {
                  const pid = normalizePresenceUserId(u.friendId);
                  const pinfo = (pid && presence[pid]) || {};
                  const isOnline = pinfo?.online === true;
                  const statusText = isOnline
                    ? t('contacts.presence.online')
                    : t('contacts.presence.offline');

                  const title = statusText;

                  return (
                    <li key={u.friendId || u.fullName} style={S.item}>
                      <div style={S.userBlock}>
                        <img
                          src={contactPhotoSrc(u)}
                          alt={u.fullName || t('contacts.userFallback')}
                          style={S.avatar}
                          onError={(e) => {
                            if (e.currentTarget.src.includes('logo192.png')) return;
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/logo192.png';
                          }}
                        />
                        <div style={S.userTxt}>
                          <div
                            style={{
                              ...S.name,
                              ...(pid && unread[pid] && unread[pid].count > 0 ? { fontWeight: 900, color: '#2c1c4e' } : {})
                            }}
                            title={u.fullName}
                          >
                            {u.fullName}
                          </div>
                          {/* Badge de no leídos */}
                          {pid && unread[pid] && unread[pid].count > 0 && (
                            <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'nowrap' }}>
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minWidth: 18,
                                  height: 18,
                                  padding: '0 6px',
                                  borderRadius: 999,
                                  background: 'linear-gradient(135deg,#DAA520,#f4c84a)',
                                  color: '#4B0082',
                                  fontWeight: 900,
                                  fontSize: 11,
                                  boxShadow: '0 8px 18px rgba(218,165,32,0.35)',
                                }}
                                title={t('contacts.unreadMessages')}
                              >
                                {unread[pid].count}
                              </span>
                              {unread[pid].lastText && (
                                <span
                                  style={{
                                    fontSize: 12,
                                    color: '#5a4b7a',
                                    minWidth: 0,
                                    maxWidth: 'min(55vw, 260px)',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    lineHeight: 1.2,
                                  }}
                                  title={unread[pid].lastText}
                                >
                                  {unread[pid].lastText}
                                </span>
                              )}
                            </div>
                          )}
                          <div style={S.metaRow}>
                            <span title={title}>
                              <span
                                style={{
                                  display: "inline-block",
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  backgroundColor: isOnline ? "#22c55e" : "#9ca3af",
                                  boxShadow: isOnline ? "0 0 0 3px rgba(34,197,94,.2)" : "none",
                                }}
                              />
                            </span>
                            <span style={S.idHint}>{statusText}</span>
                            {u.zodiacSign && <span style={S.chip}>{u.zodiacSign}</span>}
                          </div>
                          {u.bio && <div style={S.bio}>{u.bio}</div>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <button
                          style={S.btnPrimary}
                          onClick={() => onChat(u.friendId)}
                          disabled={!u.friendId}
                          title={!u.friendId ? t('contacts.errors.invalidId') : t('contacts.actions.chat')}
                        >
                          {t('contacts.actions.chat')}
                        </button>
                        <button
                          style={S.btnDanger}
                          onClick={() => onRemove(u.friendId)}
                          disabled={!u.friendId}
                          title={t('contacts.actions.remove')}
                        >
                          {t('contacts.actions.delete')}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

        </div>
      </div>
    </div>
  );
}
